

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import TimelinePlayer from './components/TimelinePlayer';
import RightMenu, { GeneratedVoice } from './components/RightMenu';
import SidebarProjectList, { Folder, Project as SidebarProject } from './components/SidebarProjectList';
import { PasswordGate } from './components/PasswordGate';
import { MontageElements } from './components/RightMenu/ChatTab';
import { Subtitle, SubtitleStyle, Clip, Track, EditingSuggestion, MontageVersion, ChatMessage, SavedProject, VideoVersion, MasterTemplate, AudioSource, ImageMask, ChatPendingChoice } from './types';
import { Type } from "@google/genai";
import { AdminDashboard } from './components/AdminDashboard';
import { AudioExtractionModal } from './components/AudioExtractionModal';
import { InitialDropzone } from './components/InitialDropzone';
import { createNewClip, getMediaDurationFromUrl, generateThumbnails, generateWaveformData, generateThumbnailFromUrl, floatTo16BitPCM, encodeWav, generateThumbnail, formatTimecode, parseSRT, srtTimeToSeconds, secondsToSrtTime, extractFirstFrame, parseAiJsonResponse } from './utils';
import { auth, firestore } from './firebase';
// FIX: Switched to Firebase v8 compat API to resolve module export errors.
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
// FIX: Import Firebase functions compat module to make HttpsCallableResult type available.
import 'firebase/compat/functions';
// FIX: Consistently use compat API to avoid ReferenceError: L conflicts
import {
  callGemini,
  getUploadUrl,
  getStreamUploadUrl,
  getPresignedUrl,
  getR2UploadUrl,
  exportVideo,
  transcribeAudio,
  transcribeAudioConstance,
  translateSubtitles,
  translateSubtitlesBatch,
  enhanceAudio,
  deleteFirebaseFile,
  withRetry,
  toggleEyeContact,
  enhanceVideo,
  checkEnhanceStatus,
  initiateMultipartUpload,
  getMultipartUploadUrls,
  completeMultipartUpload,
  functions
} from './services/firebase-api';
import { uploadFileToR2 } from './services/upload-manager';
import { startExport, ExportError, ExportManager } from './export-manager';
import { AspectRatio, ASPECT_RATIOS } from './media-recorder-exporter';
import { getConverter } from './webm-to-mp4-converter';
import { FFmpegService } from './ffmpeg-service';
import { fetchFile } from '@ffmpeg/util';
import { saveProjectToFirestore, getProjectsFromFirestore, deleteProjectFromFirestore, renameProjectInFirestore, getProjectById, shareProject, saveMasterTemplate } from './firestore';
import { RelinkModal } from './components/RelinkModal';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2 } from 'lucide-react';
import { generateSRT, formatSRTTime } from './utils/srt';
import {
  buildAutoMontageForkPlan,
  getMontageFingerprint,
  isOriginalMontageVersion
} from './utils/montage-versioning';
import { updateClipDeep, findClipDeep, getClipLocalSourceUrl, getClipSourceUrl, mapClipsDeep, flattenClipsDeep, getRemoteVideoClipsNeedingLocalCache } from './utils/clip-helpers';
import { fixSubtitleOverlaps, filterSubtitlesByRange } from './utils/subtitle-helpers';
import { computeMaskOverlayTransform } from './utils/image-mask-placement';
import { getServerExportClient, ExportJobStatus as ServerExportJobStatus } from './server-export-client';
import { DEFAULT_LIBRARY } from './components/RightMenu/constants';

// Custom Hooks
import { useClipManagement } from './hooks/useClipManagement';
import { useProjectState } from './hooks/useProjectState';
import { useExportManager } from './hooks/useExportManager';
import { useChat } from './hooks/useChat';
import { useMagicProcess } from './hooks/useMagicProcess';
import { useImageMask } from './hooks/useImageMask';
import { useAssetPreloader } from './hooks/useAssetPreloader';


// Let TypeScript know about the global lamejs object from the script tag
declare const lamejs: any;

interface ExportedVideo {
  id: string;
  name: string;
  urlWebM: string;
  urlMP4?: string;
  thumbnailUrl?: string;
  mp4ConversionProgress?: number; // 0-100, undefined si pas en conversion
  srtContent?: string; // Contenu SRT pour preview avec <track>
}

const INITIAL_TRACKS: Track[] = [
  { id: 't1', label: 'Overlays', accepts: ['text', 'image', 'synthe', 'panel'], isMagnetic: false, isVisible: true, isMuted: true },
  { id: 's1', label: 'Split', accepts: ['video', 'image', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
  { id: 'v1', label: '1', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
  { id: 'v2', label: '2', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
  { id: 'z1', label: 'Zoom', accepts: ['zoom'], isMagnetic: false, isVisible: true, isMuted: true },
];

// Default structure for new Synthés: 2 text elements (name + title) positioned like a lower-third
const DEFAULT_SYNTHE_CLIPS: Clip[] = [
  {
    id: `synthe-text-name-${Date.now()}`,
    name: 'Nom',
    type: 'text',
    trackId: 't1',
    start: 0,
    duration: 5,
    originalDuration: 5,
    trimStart: 0,
    text: 'Nom Prénom',
    style: { fontFamily: 'Futura, sans-serif', fontSize: 42, color: '#FFFFFF', textAlign: 'left' as const, textBaseline: 'middle' as const },
    transform: { x: -300, y: 150, scale: 1, rotation: 0 },
    opacity: 1,
    fadeInDuration: 0.3,
    fadeOutDuration: 0.3,
    volume: 1,
  } as Clip,
  {
    id: `synthe-text-title-${Date.now() + 1}`,
    name: 'Titre',
    type: 'text',
    trackId: 't1',
    start: 0,
    duration: 5,
    originalDuration: 5,
    trimStart: 0,
    text: 'Fonction / Titre',
    style: { fontFamily: 'Futura, sans-serif', fontSize: 28, color: '#CCCCCC', textAlign: 'left' as const, textBaseline: 'middle' as const },
    transform: { x: -300, y: 210, scale: 1, rotation: 0 },
    opacity: 1,
    fadeInDuration: 0.3,
    fadeOutDuration: 0.3,
    volume: 1,
  } as Clip,
];

// Default structure for new Panels: background image + centered uppercase blue text
const DEFAULT_PANEL_CLIPS: Clip[] = [
  // Background image
  {
    id: `panel-bg-${Date.now()}`,
    name: 'Background',
    type: 'image',
    trackId: 'a1',
    start: 0,
    duration: 5,
    originalDuration: 5,
    trimStart: 0,
    url: 'https://medias.storystudio.cc/IntroOutro/BG.png',
    transform: { x: 0, y: 0, scale: 1, rotation: 0 },
    opacity: 1,
    fadeInDuration: 0,
    fadeOutDuration: 0,
    volume: 1,
  } as Clip,
  // Centered uppercase blue text
  {
    id: `panel-text-title-${Date.now()}`,
    name: 'Title',
    type: 'text',
    trackId: 't1',
    start: 0,
    duration: 5,
    originalDuration: 5,
    trimStart: 0,
    text: 'VOTRE TEXTE ICI',
    style: { fontFamily: 'Futura, sans-serif', fontSize: 80, color: '#0066FF', textAlign: 'center' as const, textBaseline: 'middle' as const },
    transform: { x: 0, y: 0, scale: 1, rotation: 0 },
    opacity: 1,
    fadeInDuration: 0.5,
    fadeOutDuration: 0.5,
    volume: 1,
  } as Clip,
];

// Firebase API functions are now imported from services/firebase-api.ts

/**
 * Tracks and updates an aggregate upload progress message in the chat.
 */
const updateAggregateUploadProgress = (
  messageId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  fileName: string,
  current: number,
  total: number,
  isComplete: boolean = false
) => {
  setMessages(prev => {
    const existing = prev.find(m => m.id === messageId);
    const progress = Math.round((current / total) * 100);
    const statusText = isComplete
      ? `✅ Téléversement de ${total} fichier(s) terminé !`
      : `☁️ Téléversement en cours... (${current}/${total}) - ${fileName}`;

    if (existing) {
      return prev.map(m => m.id === messageId ? { ...m, statusText, progress, isLoading: !isComplete } : m);
    }

    return [...prev, {
      id: messageId,
      role: 'assistant',
      text: "",
      isLoading: !isComplete,
      statusText,
      progress
    }];
  });
};
// Firebase API functions imported above from services/firebase-api.ts

// withRetry helper imported from services/firebase-api.ts

// SRT generation functions imported from utils/srt.ts

export default function App() {

  // URL-based project loading
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('account');
  const timelinePlayerRef = useRef<{
    seek: (time: number) => void;
    getCurrentTime: () => number;
  }>(null);

  const [isInitialDropzoneVisible, setIsInitialDropzoneVisible] = useState(false);
  // FIX: Use User type from firebase namespace
  const [user, setUser] = useState<firebase.User | null>(null);

  // Export manager ref for cancellation (managed in useExportState but ref needed here if passed explicitly? No, useExportState exposes it)
  // We keep exportManagerRef here if it was used outside hooks, but useExportState exposes it.
  // Actually, App.tsx uses exportManagerRef.current to abort.
  // We should use the one from useExportState.

  // ============================================================================
  // CUSTOM HOOKS STATE MANAGMENT
  // ============================================================================

  // 1. Clip & Timeline Management
  const {
    allClips, setAllClips,
    tracks, setTracks,
    selectedClipId, setSelectedClipId,
    isClipContextMenuOpen, setIsClipContextMenuOpen,
    handleClipSelect,
    history, historyIndex, undo, redo,
    navigationStack, setNavigationStack,
    currentScope,
    updateClips: baseUpdateClips,
    cachedSourceClips,
    setCachedSourceClips,
    INITIAL_TRACKS
  } = useClipManagement();

  // Global Keyboard Shortcuts (Undo/Redo)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? e.metaKey : e.ctrlKey;

      if (modifierKey && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
      } else if (modifierKey && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // 4. Chat & AI
  const {
    messages, setMessages,
    editingSuggestions, setEditingSuggestions,
    addUserMessage, addAssistantMessage, updateMessage
  } = useChat();

  const contextualEditingSuggestions = useMemo<EditingSuggestion[]>(() => {
    const hasClips = allClips.length > 0;
    const hasVideo = allClips.some(c => c.type === 'video');
    const hasSubtitles = allClips.some(c => c.subtitles && Object.keys(c.subtitles).some(lang => lang !== 'none' && c.subtitles![lang]?.length > 0));

    if (!hasClips) {
      return [
        { id: '1', label: 'Ajouter une vidéo', instruction: 'Comment ajouter une vidéo depuis ma bibliothèque ?' },
        { id: '2', label: 'Que peux-tu faire ?', instruction: 'Quelles sont tes capacités en tant qu\'assistant de montage ?' }
      ];
    }

    if (hasVideo && !hasSubtitles) {
      return [
        { id: '1', label: 'Sous-titrer la vidéo', instruction: 'Génère les sous-titres pour cette vidéo.' },
        { id: '2', label: 'Créer une intro', instruction: 'Ajoute un synthé d\'introduction au début de la vidéo.' }
      ];
    }

    if (hasSubtitles) {
      return [];
    }

    return [];
  }, [allClips]);

  // Magic Process code moved below activeSubtitleLanguage definition

  // Montage Versions State (must be declared before useProjectState to pass as prop)
  const [montageVersions, setMontageVersions] = useState<MontageVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const montageVersionsRef = useRef<MontageVersion[]>([]);
  const currentVersionIdStateRef = useRef<string | null>(null);
  const autoMontageForkBypassRef = useRef(false);

  useEffect(() => {
    montageVersionsRef.current = montageVersions;
  }, [montageVersions]);

  useEffect(() => {
    currentVersionIdStateRef.current = currentVersionId;
  }, [currentVersionId]);

  const runWithoutAutoMontageFork = useCallback((callback: () => void) => {
    autoMontageForkBypassRef.current = true;
    try {
      callback();
    } finally {
      window.setTimeout(() => {
        autoMontageForkBypassRef.current = false;
      }, 0);
    }
  }, []);

  const updateClips = useCallback((
    updaterOrValue: React.SetStateAction<Clip[]>,
    saveToHistory: boolean = true
  ) => {
    if (
      autoMontageForkBypassRef.current ||
      !saveToHistory ||
      navigationStack.length > 0 ||
      allClips.length === 0
    ) {
      baseUpdateClips(updaterOrValue, saveToHistory);
      return;
    }

    const nextClips = typeof updaterOrValue === 'function'
      ? (updaterOrValue as (prev: Clip[]) => Clip[])(allClips)
      : updaterOrValue;

    if (getMontageFingerprint(nextClips) === getMontageFingerprint(allClips)) {
      baseUpdateClips(nextClips, saveToHistory);
      return;
    }

    const activeVersion = montageVersionsRef.current.find(
      version => version.id === currentVersionIdStateRef.current
    ) || null;

    if (!currentVersionIdStateRef.current || isOriginalMontageVersion(activeVersion)) {
      const { nextVersions, nextVersionId } = buildAutoMontageForkPlan(
        montageVersionsRef.current,
        allClips
      );

      montageVersionsRef.current = nextVersions;
      currentVersionIdStateRef.current = nextVersionId;
      setMontageVersions(nextVersions);
      setCurrentVersionId(nextVersionId);
    }

    baseUpdateClips(nextClips, saveToHistory);
  }, [allClips, baseUpdateClips, navigationStack.length]);

  // Centralized subtitle state (needed before useExportManager)
  const [subtitleStyle, setSubtitleStyle] = useState<SubtitleStyle>({
    fontFamily: 'Arial, sans-serif',
    fontSize: 30,
    color: '#FFFFFF',
    shadowType: 'outline',
    outlineWidth: 1.5,
    positionY: 10,
    padding: 5,
  });
  const [activeSubtitleLanguage, setActiveSubtitleLanguage] = useState('fr');

  // NEW: Magic Process State (Moved here to access activeSubtitleLanguage)
  const { state: magicState, startMagicProcess: startMagicProcessHook, addLog: addMagicLog } = useMagicProcess();
  const [magicClipId, setMagicClipId] = useState<string | null>(null);

  // Magic Process Side Effects
  useEffect(() => {
    if (!magicClipId) return;

    // 1. Update Video URL if optimized
    if (magicState.optimizedUrl) {
      updateClips(prev => updateClipDeep(prev, magicClipId, (c) => {
        if (c.url === magicState.optimizedUrl) return c;
        return { ...c, url: magicState.optimizedUrl! };
      }));
    }

    // 2. Update Subtitles if generated
    if (magicState.subtitleSegments && magicState.subtitleSegments.length > 0) {
      updateClips(prev => updateClipDeep(prev, magicClipId, (c) => {
        return {
          ...c,
          subtitles: {
            [activeSubtitleLanguage || 'fr']: magicState.subtitleSegments
          }
        };
      }));
    }

    // 3. Add Analysis to Chat (Once)
    if (magicState.analysisResult && !messages.some(m => m.text.includes(magicState.analysisResult!.substring(0, 20)))) {
      addAssistantMessage(`🧠 **Analyse LVMH pour le clip**: \n\n${magicState.analysisResult}`);
    }

  }, [magicState.optimizedUrl, magicState.subtitleSegments, magicState.analysisResult, magicClipId, updateClips, addAssistantMessage, messages, activeSubtitleLanguage]);

  const handleStartMagicProcess = (file: File) => {
    if (selectedClipId) {
      setMagicClipId(selectedClipId);
      startMagicProcessHook(file);
    } else {
      alert("Veuillez sélectionner un clip.");
    }
  };

  // Ref to bridge exportedVideos from useExportManager to useProjectState auto-save
  const exportedVideosRef = useRef<any[]>([]);

  // 2. Project State (Needs data from other hooks)
  const {
    currentProjectId, setCurrentProjectId,
    currentProjectIdRef,
    savedProjects, setSavedProjects,
    folders, setFolders,
    rootProjects, setRootProjects,
    sidebarOpen, setSidebarOpen,
    markProjectDeleted
  } = useProjectState({
    user,
    allClips,
    tracks,
    montageVersions,
    messages,
    editingSuggestions,
    exportedVideosRef
  });

  // 3. Export State & Handlers (After useProjectState to get dependencies)
  const {
    isExporting, setIsExporting,
    exportStatus, setExportStatus,
    exportProgress, setExportProgress,
    exportDetails, setExportDetails,
    exportedVideos, setExportedVideos,
    exportAspectRatio, setExportAspectRatio,
    scaleMode, setScaleMode,
    vfxSegmentPreviews, setVfxSegmentPreviews,
    serverExportStatus,
    exportManagerRef,

    resetExportState,
    isTimelineEligibleForFastExport,
    handleStartExport,
    handleCancelExport,
    handleStartServerExport,
    handleCancelServerExport,
    handleDeleteExport,
    checkActiveExportJobs,
    uploadAssetToStorage,
    activeServerExports,
    chunks,
    isMerging,
    retryChunkDownload,
    handleStartHybridExport,
    hasTimelineOverlays,
    totalTimelineDuration,
  } = useExportManager({
    allClips,
    tracks,
    activeSubtitleLanguage,
    subtitleStyle,
    user,
    currentProjectId,
    currentProjectIdRef,
    savedProjects,
    setSavedProjects,
    updateClips
  });

  // Sync exportedVideos ref for auto-save
  useEffect(() => {
    exportedVideosRef.current = exportedVideos;
  }, [exportedVideos]);

  // Preload Library Assets
  const { preloadedAssets, isComplete: isLibraryAssetsComplete } = useAssetPreloader();

  // Image Mask State (Nano Banana)
  const { handleGenerateImageMask, handleToggleImageMask, handleDeleteImageMask } = useImageMask({
    onClipsChange: updateClips,
    exportServiceUrl: 'https://export-service-400620783493.us-central1.run.app' // Production Cloud Run Service
  });

  const handleGenerateImageMaskFromCanvas = async (
    clip: Clip,
    prompt: string,
    customBlobOrUseActiveFrame?: boolean | Blob,
    cropData?: ImageMask['cropRect']
  ) => {
    let customBlob: Blob | undefined;
    if (typeof customBlobOrUseActiveFrame === 'boolean' && customBlobOrUseActiveFrame && timelinePlayerRef.current) {
      const capturedBlob = await timelinePlayerRef.current.captureFrame();
      if (capturedBlob) {
        customBlob = capturedBlob;
      } else {
        console.warn("Could not capture frame from canvas.");
      }
    } else if (customBlobOrUseActiveFrame instanceof Blob) {
      customBlob = customBlobOrUseActiveFrame;
    }
    await handleGenerateImageMask(clip, prompt, customBlob, cropData);
  };

  const handleCaptureFrameForMask = async (clip: Clip, useActiveFrame?: boolean): Promise<Blob | null> => {
    if (useActiveFrame && timelinePlayerRef.current) {
      const capturedBlob = await timelinePlayerRef.current.captureFrame();
      if (capturedBlob) return capturedBlob;
    }
    const sourceUrl = clip.type === 'video'
      ? getClipSourceUrl(clip, { preferLocal: true, includeStreamUrl: true, allowStreamingManifest: false })
      : (clip.localUrl || clip.url);
    if (!sourceUrl) return null;
    return await extractFirstFrame(sourceUrl);
  };

  const handleAddImageMaskToTimeline = useCallback((sourceClip: Clip, mask: ImageMask) => {
    const transform = computeMaskOverlayTransform(sourceClip, mask);

    // Always target the Overlays track (t1) for mask clips.
    // The "find track above" logic previously could land on s1 (Split track) when
    // the source clip is on v1, causing the mask to be treated as a split-screen
    // clip rather than a standard image overlay — making it invisible or broken.
    // t1 is the dedicated overlay track that accepts 'image' clips.
    const targetTrackId = 't1';

    const newImageClip: Clip = {
      ...createNewClip({ type: 'image', url: mask.url }),
      name: mask.prompt ? `Gen: ${mask.prompt.substring(0, 15)}...` : `Mask`,
      start: sourceClip.start,
      duration: sourceClip.duration,
      trackId: targetTrackId,
      transform
    };

    updateClips(prev => [...prev, newImageClip]);
  }, [updateClips]);

  // Export state managed by useExportManager hook
  const [translationsInProgress, setTranslationsInProgress] = useState<Set<string>>(new Set());

  // Project Loading Transition State
  const [projectLoadingState, setProjectLoadingState] = useState<{ isLoading: boolean; thumbnailUrl?: string }>({ isLoading: false });

  // Generated Voiceovers State (persisted with project)
  const [generatedVoices, setGeneratedVoices] = useState<GeneratedVoice[]>([]);
  const [voiceoverText, setVoiceoverText] = useState("");

  // Chat state managed by useChat hook

  // Transcription mode toggle (GPT-4o Mini vs Whisper)
  const [useGpt4oTranscribe, setUseGpt4oTranscribe] = useState(() => {
    return localStorage.getItem('transcriptionMode') === 'gpt4o';
  });

  const [currentMontageElements, setCurrentMontageElements] = useState<MontageElements>({
    intro: false, outro: false, synthe: false, panel: false
  });

  // Auto-save when montageVersions changes

  // Auto-save is handled by useProjectState hook


  // cachedSourceClips managed by useClipManagement hook

  // Audio extraction modal state
  const [audioExtractionState, setAudioExtractionState] = useState<{
    isVisible: boolean;
    fileName: string;
    progress: number;
    statusMessage: string;
  }>({
    isVisible: false,
    fileName: '',
    progress: 0,
    statusMessage: '',
  });

  // State managed by useProjectState hook

  // Auto-switch to Master tab when entering restricted mode (Synthe/Panel)
  useEffect(() => {
    if (currentScope?.type === 'synthe' || currentScope?.type === 'panel') {
      setActiveTab('master');
    }
  }, [currentScope?.id, currentScope?.type]);
  // user moved to top
  // savedProjects removed
  // savedProjects, folders, rootProjects removed and provided by hook

  // Relink state
  const [missingFiles, setMissingFiles] = useState<string[]>([]);
  const [isRelinkModalOpen, setIsRelinkModalOpen] = useState(false);

  // Sidebar state
  // sidebarOpen provided by hook

  // Add search state
  const [searchQuery, setSearchQuery] = useState('');

  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [isDraggingProject, setIsDraggingProject] = useState(false);

  // AUTO-SAVE LOGIC: Managed by useProjectState hook



  const selectedClip = useMemo(() => {
    return currentScope.clips.find(c => c.id === selectedClipId) || null;
  }, [currentScope.clips, selectedClipId]);

  // Track clips for which thumbnail generation has been attempted (to avoid infinite loops)
  const thumbnailAttemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Show dropzone only if there are no media clips (video, image, audio) on the timeline
    setIsInitialDropzoneVisible(
      allClips.filter(c => c.type === 'video' || c.type === 'image' || c.type === 'audio').length === 0
    );

    // AUTO-REGENERATE THUMBNAILS: If a video clip has a valid URL but no thumbnails, 
    // regenerate them (e.g., after loading from Firestore or if they were stale).
    allClips.forEach(clip => {
      const hasValidUrl = clip.url && !clip.url.startsWith('file-requirement:');
      // Auto-fix old clips saved as video but with image URLs (e.g., emoji PNGs)
      const isImageUrl = clip.url && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(clip.url);
      if (clip.type === 'video' && isImageUrl && hasValidUrl) {
        setAllClips(prev => prev.map(c => c.id === clip.id ? { ...c, type: 'image' as const } : c));
        return; // Skip thumbnail generation for this clip
      }
      const needsThumbnails = clip.type === 'video' && (!clip.thumbnails || clip.thumbnails.length === 0);
      const alreadyAttempted = thumbnailAttemptedRef.current.has(clip.id);
      // Skip very long videos (> 30 min) - they're too large for client-side thumbnail generation
      const isTooLong = clip.originalDuration && clip.originalDuration > 1800;

      if (hasValidUrl && needsThumbnails && !alreadyAttempted && !isTooLong) {
        thumbnailAttemptedRef.current.add(clip.id);
        console.log(`[AutoThumb] Regenerating thumbnails for ${clip.name}...`);
        generateThumbnails(clip.url!).then(thumbnails => {
          if (thumbnails && thumbnails.length > 0) {
            // We use the functional update directly on setAllClips to avoid 
            // unnecessary re-renders or infinite loops if updateClips adds to history
            setAllClips(prev => prev.map(c => c.id === clip.id ? { ...c, thumbnails } : c));
          }
        }).catch(err => {
          console.warn(`[AutoThumb] Failed for ${clip.name}:`, err);
        });
      } else if (isTooLong && needsThumbnails && !alreadyAttempted) {
        thumbnailAttemptedRef.current.add(clip.id);
        console.log(`[AutoThumb] Skipping ${clip.name} - video too long (${Math.round(clip.originalDuration! / 60)}min) for client-side thumbnail generation`);
      }
    });

    // AUTO-HEAL PROJECT THUMBNAIL: Managed by useProjectState hook
  }, [allClips]);

  // AUTO-SHOW TRACKS: When clips are added to t1 (Overlays) or s1 (Split), auto-show those tracks
  useEffect(() => {
    const hasT1Clips = allClips.some(c => c.trackId === 't1');
    const hasS1Clips = allClips.some(c => c.trackId === 's1');

    setTracks(prev => prev.map(track => {
      if (track.id === 't1' && hasT1Clips && !track.isVisible) {
        return { ...track, isVisible: true };
      }
      if (track.id === 's1' && hasS1Clips && !track.isVisible) {
        return { ...track, isVisible: true };
      }
      return track;
    }));
  }, [allClips]);


  // SYNC CLIPS TO MONTAGE VERSION
  useEffect(() => {
    if (currentVersionId) {
      setMontageVersions(prev => prev.map(v =>
        v.id === currentVersionId ? { ...v, clips: allClips } : v
      ));
    }
  }, [allClips, currentVersionId]);

  // AUTO-SAVE USER SETTINGS: Persist folder structure and project organization

  // User settings auto-save is now handled in useProjectState


  useEffect(() => {
    // This effect should ONLY run on component unmount to prevent memory leaks from blob URLs.
    return () => {
      const clipBlobUrls = new Set<string>();
      // Revoke URLs from all clips on the timeline
      allClips.forEach(clip => {
        const localUrls = [clip.url, clip.localUrl].filter((url): url is string => !!url && url.startsWith('blob:'));
        localUrls.forEach(url => clipBlobUrls.add(url));
        if (clip.thumbnails) {
          clip.thumbnails.forEach(thumb => {
            if (thumb && thumb.startsWith('blob:')) {
              URL.revokeObjectURL(thumb);
            }
          });
        }
      });
      clipBlobUrls.forEach(url => URL.revokeObjectURL(url));
      // Revoke URLs from all exported videos
      exportedVideos.forEach(video => {
        if (video.urlWebM && video.urlWebM.startsWith('blob:')) {
          URL.revokeObjectURL(video.urlWebM);
        }
        if (video.urlMP4 && video.urlMP4.startsWith('blob:')) {
          URL.revokeObjectURL(video.urlMP4);
        }
        if (video.thumbnailUrl && video.thumbnailUrl.startsWith('blob:')) {
          URL.revokeObjectURL(video.thumbnailUrl);
        }
      });
    };
    // CRITICAL FIX: Empty dependency array ensures this cleanup runs ONLY on component unmount.
    // Using [allClips, exportedVideos] would incorrectly run it on every state change,
    // revoking URLs for clips that are still in use.
  }, []);

  useEffect(() => {
    // FIX: Use auth.onAuthStateChanged from compat API
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        setActiveTab('assistant');
        // Clear initial messages and show connecting status
        setMessages([{
          id: 'gemini-status-loading',
          role: 'assistant',
          text: "Connexion à Vertex AI..."
        }]);

        try {
          // FIX: Explicitly type HttpsCallableResult to resolve type inference issue.
          const result: firebase.functions.HttpsCallableResult = await callGemini({ model: 'ping' });
          if ((result.data as any)?.status === 'ready') {
            setMessages(prev => {
              // Prevent duplicate welcome messages on page reload
              if (prev.some(m => m.id === 'gemini-status-ready')) {
                return prev.filter(m => m.id !== 'gemini-status-loading');
              }
              return [
                ...prev.filter(m => m.id !== 'gemini-status-loading'),
                {
                  id: 'gemini-status-ready',
                  role: 'assistant',
                  text: "Connecté à Vertex AI. Je suis prêt ! Que souhaitez-vous créer aujourd'hui ?"
                }
              ];
            });
          } else {
            throw new Error("Invalid response from ping");
          }
        } catch (error) {
          console.error("Gemini health check failed:", error);
          setMessages(prev => [
            ...prev.filter(m => m.id !== 'gemini-status-loading'), // Remove loading message
            {
              id: 'gemini-status-error',
              role: 'assistant',
              text: "⚠️ Les fonctionnalités d'IA (sous-titres, montage, voix-off) OUT"
            }
          ]);
        }

        // Précharger ffmpeg en arrière-plan pour la conversion MP4
        console.log('[App] Préchargement de ffmpeg en arrière-plan...');
        getConverter().load().then(() => {
          console.log('[App] ffmpeg (convertisseur) préchargé avec succès');
        }).catch(err => {
          console.error('[App] Erreur lors du préchargement de ffmpeg (convertisseur):', err);
        });

        // Précharger FFmpeg pour les exports ConcatExporter
        console.log('[App] Préchargement de FFmpeg (export) en arrière-plan...');
        FFmpegService.preload().then(() => {
          console.log('[App] FFmpeg (export) préchargé avec succès ✓');
        }).catch(err => {
          console.error('[App] Erreur lors du préchargement de FFmpeg (export):', err);
        });
      } else {
        // User is logged out
        setActiveTab('account');
        setMessages([
          { id: 1, role: "assistant", text: "Bonjour ! Connectez-vous pour commencer." }
        ]);
      }
    });
    return () => unsubscribe();
  }, []);

  // updateClips provided by useClipManagement hook

  const handleClipVolumeChange = (clipId: string, volume: number, saveToHistory: boolean = false) => {
    updateClips(prev => prev.map(c => c.id === clipId ? { ...c, volume } : c), saveToHistory);
  };

  /**
   * Safe clip updater that handles background tasks (R2, Transcription)
   * regardless of which project is currently active.
   * Updates both the UI (if project active) and persistence (Firestore + State).
   */
  const updateClipSafe = useCallback((targetProjectId: string, clipId: string, updater: (c: Clip) => Clip) => {
    const updateMontageVersions = (versions?: MontageVersion[]) =>
      versions?.map(version => ({
        ...version,
        clips: updateClipDeep(version.clips || [], clipId, updater)
      }));

    // 1. Update Persistence (Firestore + SavedProjects State)
    setSavedProjects(prev => {
      // Optimistic update for UI (only if strictly necessary for sidebar info)
      // But we must NOT use this object for saving if it's incomplete.
      return prev.map(p => {
        if (p.id === targetProjectId) {
          // Just update local state for UI responsiveness (e.g. clip name change in sidebar if displayed)
          const newClips = updateClipDeep(p.clips || [], clipId, updater);
          const newMontageVersions = updateMontageVersions(p.montageVersions);
          return {
            ...p,
            clips: newClips,
            ...(newMontageVersions ? { montageVersions: newMontageVersions } : {}),
            updatedAt: Date.now()
          };
        }
        return p;
      });
    });

    // CRITICAL FIX: Fetch FULL project from Firestore before saving to avoid overwriting with incomplete SidebarProject
    // This runs in background
    (async () => {
      try {
        const { getFirestore, doc, getDoc, updateDoc } = await import('firebase/firestore');
        const { auth } = await import('./firebase');
        const db = getFirestore();
        const user = auth.currentUser;
        if (!user) return;

        const projectRef = doc(db, 'users', user.uid, 'projects', targetProjectId);
        const snapshot = await getDoc(projectRef);

        if (snapshot.exists()) {
          const fullProject = snapshot.data() as SavedProject;

          // Apply update to the FULL project's clips
          // Note: We might need to handle montages too if the clip is inside one? 
          // updateClipDeep handles array of clips. Check if clip is in root clips or montages.

          const newClips = updateClipDeep(fullProject.clips || [], clipId, updater);
          const newMontageVersions = updateMontageVersions(fullProject.montageVersions);

          await saveProjectToFirestore({
            ...fullProject,
            clips: newClips,
            ...(newMontageVersions ? { montageVersions: newMontageVersions } : {}),
            updatedAt: Date.now()
          });
        }
      } catch (err) {
        console.error(`[UpdateClipSafe] Failed to save project ${targetProjectId}`, err);
      }
    })();

    // 2. Update Active UI ONLY IF the user is currently viewing this project
    if (currentProjectIdRef.current === targetProjectId) {
      updateClips(prevClips => updateClipDeep(prevClips, clipId, updater), false); // Do not add background updates to undo history
    }
  }, [updateClips]);

  // handleClipSelect provided by hook.
  // Add effect to switch tab on audio selection
  useEffect(() => {
    if (selectedClipId) {
      const clip = findClipDeep(currentScope.clips, selectedClipId); // We assume current scope is enough? Or deep find in allClips?
      // Actually handleClipSelect in hook updates state.
      // We can use the selectedClipId to find the clip.
      // But wait, finding clip by ID in allClips is expensive if done every render.
      // Let's rely on the user interaction to switch tab.
      // Since handleClipSelect is removed, we need to wire the hook's handleClipSelect to the UI.
      // But we also wanted the side effect (setActiveTab).
      // So we WRAP the hook's handler.
    }
  }, [selectedClipId]);

  // Wrapper for clip selection to handle UI side effects
  const onClipSelect = useCallback((clip: Clip | null) => {
    handleClipSelect(clip); // Call hook

    if (clip) {
      setActiveTab('assistant');
      // Open the clip context menu for audio/video clips (shows volume slider, audio sources)
      const isAudioClip = clip.type === 'audio' || (clip.type === 'video' && !clip.url?.toLowerCase().endsWith('.gif'));
      if (isAudioClip) {
        setIsClipContextMenuOpen(true);
      }
    } else {
      setIsClipContextMenuOpen(false);
    }
  }, [handleClipSelect, setActiveTab]);

  const handleCloseClipContextMenu = useCallback(() => {
    setIsClipContextMenuOpen(false);
    setActiveTab('assistant');
  }, [setActiveTab]);

  // FOLDER/SETTINGS LOADING: Handled exclusively by useProjectState.ts hook.
  // DO NOT duplicate here — it creates a race condition that overwrites folders with [].
  // See useProjectState.ts for the canonical load + auto-save logic.

  const handleRelinkFiles = useCallback(async (files: File[]): Promise<void> => {
    console.log("Relink files disabled temporarily");
  }, []);

  // Save project function
  const saveProject = useCallback(async () => {
    if (!user) {
      setActiveTab('account');
      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: 'Veuillez vous connecter pour sauvegarder vos projets.' }]);
      return;
    }

    const loadingId = Date.now();
    setMessages(prev => [...prev, {
      id: loadingId,
      role: 'assistant',
      text: 'Sauvegarde des médias et du projet...',
      isLoading: true
    }]);

    try {
      // Count total blob files to upload for progress tracking
      const countBlobFiles = (clips: Clip[]): number => {
        let count = 0;
        clips.forEach(clip => {
          if (clip.url?.startsWith('blob:')) count++;
          if (clip.syntheData?.clips) count += countBlobFiles(clip.syntheData.clips);
        });
        return count;
      };
      const totalToUpload = countBlobFiles(allClips);
      let uploadedCount = 0;

      // Update progress in chat
      const updateProgress = () => {
        const progress = totalToUpload > 0 ? Math.round((uploadedCount / totalToUpload) * 100) : 0;
        setMessages(prev => prev.map(m => m.id === loadingId ? {
          ...m,
          text: `📤 Upload des médias (${uploadedCount}/${totalToUpload})...`,
          progress
        } : m));
      };

      // 1. Recursive Media Upload
      const uploadMediaRecursive = async (clips: Clip[]): Promise<Clip[]> => {
        const updatedClips = await Promise.all(clips.map(async (clip) => {
          let updatedClip = { ...clip };

          // Handle nested clips first
          if (updatedClip.syntheData) {
            updatedClip.syntheData = {
              ...updatedClip.syntheData,
              clips: await uploadMediaRecursive(updatedClip.syntheData.clips)
            };
          }

          // Upload Blob URL to R2 (all media types including videos)
          if (updatedClip.url && updatedClip.url.startsWith('blob:')) {
            try {
              console.log(`Uploading ${updatedClip.name} to R2...`);
              const response = await fetch(updatedClip.url);
              const blob = await response.blob();

              // Determine extension
              let extension = '';
              const type = blob.type || '';
              if (type.includes('image')) extension = '.png';
              else if (type.includes('video')) extension = '.mp4';
              else if (type.includes('audio')) extension = '.wav';

              // Avoid double extension if name already has it
              const fileName = updatedClip.name.includes('.') ? updatedClip.name : `${updatedClip.name}${extension}`;

              // Generate file hash
              const fileHash = `${updatedClip.name}_${blob.size}_${blob.type}`.replace(/[^a-zA-Z0-9_.-]/g, '_');

              // Ensure Content-Type is never empty and strip parameters (e.g. ;charset=utf-8) to avoid signature mismatch
              let mimeType = blob.type || (fileName.endsWith('.mp4') ? 'video/mp4' :
                (fileName.endsWith('.png') ? 'image/png' :
                  (fileName.endsWith('.wav') ? 'audio/wav' :
                    (fileName.endsWith('.mp3') ? 'audio/mpeg' : 'application/octet-stream'))));

              if (mimeType.includes(';')) {
                mimeType = mimeType.split(';')[0];
              }

              const uploadConfig = await getR2UploadUrl({
                fileName,
                contentType: mimeType,
                fileHash
              });
              const configData = uploadConfig.data as { uploadUrl?: string; publicUrl: string; existing?: boolean };

              // If file already exists on R2, skip upload
              if (configData.existing) {
                console.log(`✅ File already exists on R2: ${updatedClip.name}`);
                const localUrl = getClipLocalSourceUrl(updatedClip);
                if (localUrl) {
                  updatedClip.localUrl = localUrl;
                }
                updatedClip.url = configData.publicUrl;
                uploadedCount++;
                updateProgress(); // Consider updating this to reflect "instant" byte progress if moved to byte-tracking
                return updatedClip;
              }

              const { uploadUrl, publicUrl } = configData as { uploadUrl: string; publicUrl: string };

              console.log(`[Upload] Starting upload for ${updatedClip.name} (${mimeType}) to ${uploadUrl}`);

              // ✨ USE XHR FOR PROGRESS TRACKING ✨
              await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrl, true);
                // CRITICAL: Content-Type header MUST match exactly what was signed
                xhr.setRequestHeader('Content-Type', mimeType);

                xhr.upload.onprogress = (event) => {
                  if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    // Update chat message with specific file progress
                    setMessages(prev => prev.map(m => m.id === loadingId ? {
                      ...m,
                      text: `📤 Upload en cours: ${updatedClip.name} (${percent}%)\nTotal: ${uploadedCount}/${totalToUpload}`
                    } : m));
                  }
                };

                xhr.onload = () => {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                  } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                  }
                };

                xhr.onerror = () => reject(new Error('Network error during upload'));
                xhr.send(blob);
              });

              const localUrl = getClipLocalSourceUrl(updatedClip);
              if (localUrl) {
                updatedClip.localUrl = localUrl;
              }
              updatedClip.url = publicUrl;
              console.log(`Uploaded ${updatedClip.name} -> ${publicUrl}`);

              uploadedCount++;
              updateProgress();
            } catch (e) {
              console.error(`Failed to upload ${updatedClip.name}`, e);
            }
          }
          return updatedClip;
        }));
        return updatedClips;
      };

      const finalClips = await uploadMediaRecursive(allClips);

      // Update UI with new URLs
      setAllClips(finalClips);

      // Generate 16/9 thumbnail from first video clip
      let thumbnailUrl = '';
      const firstVideoClip = finalClips.find(c => c.type === 'video' && c.url);

      if (firstVideoClip && firstVideoClip.url) {
        thumbnailUrl = await generateThumbnailFromUrl(firstVideoClip.url);
      }

      // Upload voiceover blobs to R2 for persistence
      const uploadedVoices = await Promise.all(
        generatedVoices
          .filter(v => v.url && !v.isLoading)
          .map(async (voice) => {
            // Skip if already an R2 URL
            if (!voice.url.startsWith('blob:')) {
              return { id: voice.id, text: voice.text, voice: voice.voice, url: voice.url, duration: voice.duration };
            }
            try {
              const response = await fetch(voice.url);
              const blob = await response.blob();
              const fileName = `voiceover_${voice.id}.mp3`;
              const fileHash = `voiceover_${voice.id}_${blob.size}`.replace(/[^a-zA-Z0-9_.-]/g, '_');

              const uploadConfig = await getR2UploadUrl({
                fileName,
                contentType: blob.type || 'audio/mpeg',
                fileHash
              });
              const configData = uploadConfig.data as { uploadUrl?: string; publicUrl: string; existing?: boolean };

              if (configData.existing) {
                console.log(`✅ Voiceover already on R2: ${voice.id}`);
                return { id: voice.id, text: voice.text, voice: voice.voice, url: configData.publicUrl, duration: voice.duration };
              }

              const { uploadUrl, publicUrl } = configData as { uploadUrl: string; publicUrl: string };
              await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: { 'Content-Type': blob.type || 'audio/mpeg' }
              });
              console.log(`Uploaded voiceover ${voice.id} -> ${publicUrl}`);
              return { id: voice.id, text: voice.text, voice: voice.voice, url: publicUrl, duration: voice.duration };
            } catch (e) {
              console.error(`Failed to upload voiceover ${voice.id}`, e);
              return null;
            }
          })
      );

      const finalVoices = uploadedVoices.filter((v): v is NonNullable<typeof v> => v !== null);

      // Update local state with R2 URLs
      if (finalVoices.length > 0) {
        setGeneratedVoices(prev => prev.map(v => {
          const uploaded = finalVoices.find(u => u.id === v.id);
          return uploaded ? { ...v, url: uploaded.url } : v;
        }));
      }

      const projectName = `Projet ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
      const newProject: SavedProject = {
        id: Date.now().toString(),
        name: projectName,
        thumbnailUrl,
        clips: finalClips,
        tracks,
        montageVersions, // Save current versions in this snapshot
        aiSuggestions: editingSuggestions.length > 0 ? editingSuggestions : undefined, // Save AI suggestions
        generatedVoices: finalVoices.length > 0 ? finalVoices : undefined,
        voiceoverText: voiceoverText || undefined,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await saveProjectToFirestore(newProject);

      // Update local state and reload list to be sure
      const fromDb = await getProjectsFromFirestore();
      setSavedProjects(fromDb);

      setMessages(prev => prev.map(m => m.id === loadingId ? {
        id: Date.now(),
        role: 'assistant',
        text: `✅ Projet "${projectName}" sauvegardé sur le cloud !`
      } : m));

    } catch (error) {
      console.error('[SavedProjects] Failed to save project:', error);
      setMessages(prev => prev.map(m => m.id === loadingId ? {
        id: Date.now(),
        role: 'assistant',
        text: '❌ Erreur lors de la sauvegarde sur le cloud.'
      } : m));
    }
  }, [user, allClips, tracks, editingSuggestions]);

  /* --- Duplicate Project --- */
  const handleDuplicateProject = useCallback(async (project: SavedProject) => {
    try {
      if (!project) return;

      const newId = Date.now().toString();
      const newTitle = `${project.name} (Copy)`;

      // Deep clone project to ensure independent instance
      const newProject: SavedProject = {
        ...project,
        id: newId,
        name: newTitle,
        // Ensure strictly new timestamp
        updatedAt: Date.now(),
        createdAt: Date.now(),
        // Deep copy arrays to break references
        clips: JSON.parse(JSON.stringify(project.clips || [])),
        tracks: JSON.parse(JSON.stringify(project.tracks || [])),
        aiSuggestions: project.aiSuggestions ? JSON.parse(JSON.stringify(project.aiSuggestions)) : undefined,
      };

      // 1. Save to Firestore
      await saveProjectToFirestore(newProject);

      // 2. Update Local State: Add to savedProjects list
      setSavedProjects(prev => [newProject, ...prev]);

      // 3. Also add to rootProjects for sidebar visibility with proper Project structure
      setRootProjects(prev => [{
        id: newId,
        title: newTitle,
        assets: [],
        updated: 'just now',
        thumbnailUrl: project.thumbnailUrl,
      }, ...prev]);

      console.log('Project duplicated and saved:', newId, newTitle);
    } catch (error) {
      console.error("Failed to duplicate project", error);
    }
  }, []);

  // Load project function
  // Helper: Replace known library URLs with preloaded blob URLs in clips (recursive for syntheData)
  const injectPreloadedUrls = useCallback((clips: Clip[]): Clip[] => {
    if (!preloadedAssets || Object.keys(preloadedAssets).length === 0) return clips;
    return clips.map(clip => {
      let modified = { ...clip };
      // Replace main clip URL
      if (modified.url && preloadedAssets[modified.url]) {
        modified.url = preloadedAssets[modified.url];
      }
      // Recursively replace in syntheData clips (panels, synthés)
      if (modified.syntheData && modified.syntheData.clips) {
        modified.syntheData = {
          ...modified.syntheData,
          clips: injectPreloadedUrls(modified.syntheData.clips)
        };
      }
      return modified;
    });
  }, [preloadedAssets]);

  const applyHydratedClipData = useCallback((targetClips: Clip[], hydratedClips: Clip[]): Clip[] => {
    const hasSubtitlePayload = (clip?: Clip) =>
      !!clip && (
        !!clip.subtitles ||
        !!clip.subtitlesUrl ||
        !!(clip as any).subtitlesHash ||
        !!(clip as any)._compactSubtitles
      );

    const hydratedById = new Map<string, Clip>();
    for (const hydratedClip of flattenClipsDeep(hydratedClips)) {
      const existing = hydratedById.get(hydratedClip.id);
      if (!existing || (hasSubtitlePayload(hydratedClip) && !hasSubtitlePayload(existing))) {
        hydratedById.set(hydratedClip.id, hydratedClip);
      }
    }

    return mapClipsDeep(targetClips, clip => {
      const hydratedClip = hydratedById.get(clip.id);
      if (!hydratedClip) return clip;

      return {
        ...clip,
        subtitles: hydratedClip.subtitles,
        subtitlesUrl: hydratedClip.subtitlesUrl,
        subtitlesHash: (hydratedClip as any).subtitlesHash,
        _compactSubtitles: (hydratedClip as any)._compactSubtitles,
      } as Clip;
    });
  }, []);

  const collectProjectClips = useCallback((project?: Pick<SavedProject, 'clips' | 'montageVersions'> | null): Clip[] => {
    if (!project) return [];
    return [
      ...(project.clips || []),
      ...(project.montageVersions?.flatMap(version => version.clips || []) || [])
    ];
  }, []);

  const mergeHydratedClipDataIntoVersions = useCallback((versions: MontageVersion[], hydratedClips: Clip[]): MontageVersion[] => {
    return versions.map(version => ({
      ...version,
      clips: applyHydratedClipData(version.clips || [], hydratedClips)
    }));
  }, [applyHydratedClipData]);

  const hydrateProjectSubtitles = useCallback(async (project: SavedProject): Promise<SavedProject> => {
    const projectClips = collectProjectClips(project);
    const clipsNeedingSubs = projectClips.filter((c: any) => !c.subtitles && (c.subtitlesUrl || c._compactSubtitles));
    if (clipsNeedingSubs.length === 0) {
      return project;
    }

    console.log(`[ProjectSubtitles] Loading ${clipsNeedingSubs.length} subtitle sets for project ${project.id}...`);
    const { loadSubtitlesForProject } = await import('./firestore');
    const hydratedProject = await loadSubtitlesForProject(JSON.parse(JSON.stringify(project)) as SavedProject);
    const hydratedClips = collectProjectClips(hydratedProject);

    setSavedProjects(prev => prev.map(p => {
      if (p.id !== project.id) return p;
      return {
        ...p,
        clips: applyHydratedClipData(p.clips || [], hydratedClips),
        ...(p.montageVersions
          ? { montageVersions: mergeHydratedClipDataIntoVersions(p.montageVersions, hydratedClips) }
          : {})
      };
    }));

    if (currentProjectIdRef.current === project.id) {
      updateClips(prev => applyHydratedClipData(prev, hydratedClips), false);
      setMontageVersions(prev => {
        const next = mergeHydratedClipDataIntoVersions(prev, hydratedClips);
        montageVersionsRef.current = next;
        return next;
      });
    }

    return hydratedProject;
  }, [applyHydratedClipData, collectProjectClips, mergeHydratedClipDataIntoVersions, setSavedProjects, updateClips]);

  const hydrateProjectLocalCache = useCallback(async (projectId: string) => {
    if (currentProjectIdRef.current !== projectId) return;

    const projectSnapshot = savedProjects.find(project => project.id === projectId);
    const clipsToScan = projectSnapshot
      ? collectProjectClips(projectSnapshot)
      : allClips;
    const remoteCandidates = getRemoteVideoClipsNeedingLocalCache(clipsToScan);
    if (remoteCandidates.length === 0) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        text: "Aucune vidéo distante à mettre en cache local pour cette session."
      }]);
      return;
    }

    const loadingId = `local-cache-${Date.now()}`;
    setMessages(prev => [
      ...prev.filter(m => m.pendingChoice?.type !== 'local-cache-choice'),
      {
        id: loadingId,
        role: 'assistant',
        text: '',
        isLoading: true,
        statusText: 'Mise en cache locale des vidéos...',
        progress: 0
      }
    ]);

    const remoteSources = Array.from(new Set(
      remoteCandidates
        .map(clip => getClipSourceUrl(clip, { preferLocal: false, includeStreamUrl: true, allowStreamingManifest: false }))
        .filter((url): url is string => !!url)
    ));

    const localUrlBySource = new Map<string, string>();
    let successCount = 0;
    let failedCount = 0;

    for (let index = 0; index < remoteSources.length; index++) {
      const sourceUrl = remoteSources[index];
      if (currentProjectIdRef.current !== projectId) return;

      try {
        const response = await fetch(sourceUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        localUrlBySource.set(sourceUrl, URL.createObjectURL(blob));
        successCount++;
      } catch (error) {
        failedCount++;
        console.warn(`[LocalCache] Failed to cache ${sourceUrl}`, error);
      }

      const progress = Math.round(((index + 1) / remoteSources.length) * 100);
      setMessages(prev => prev.map(m =>
        m.id === loadingId ? { ...m, progress, statusText: `Mise en cache locale des vidéos... ${index + 1}/${remoteSources.length}` } : m
      ));
    }

    if (currentProjectIdRef.current !== projectId) return;

    if (localUrlBySource.size > 0) {
      const attachLocalUrls = (clips: Clip[]) => mapClipsDeep(clips, clip => {
        if (clip.type !== 'video') return clip;
        const sourceUrl = getClipSourceUrl(clip, { preferLocal: false, includeStreamUrl: true, allowStreamingManifest: false });
        const localUrl = sourceUrl ? localUrlBySource.get(sourceUrl) : undefined;
        if (!localUrl || clip.localUrl === localUrl) return clip;
        return { ...clip, localUrl };
      });

      updateClips(prev => attachLocalUrls(prev));
      setSavedProjects(prev => prev.map(project => {
        if (project.id !== projectId) return project;
        return {
          ...project,
          clips: attachLocalUrls(project.clips || []),
          montageVersions: project.montageVersions?.map(version => ({
            ...version,
            clips: attachLocalUrls(version.clips || [])
          })) || []
        };
      }));
    }

    const completionMessage = successCount > 0
      ? `Cache local prêt pour ${successCount}/${remoteSources.length} vidéo(s). Le preview et l'export client utiliseront la source locale pendant cette session.`
      : `Le cache local n'a pas pu être préparé${failedCount > 0 ? ` (${failedCount} échec(s))` : ''}.`;

    setMessages(prev => [
      ...prev.filter(m => m.id !== loadingId),
      { id: Date.now(), role: 'assistant', text: completionMessage }
    ]);
  }, [allClips, collectProjectClips, currentProjectIdRef, savedProjects, setSavedProjects, updateClips]);

  const loadProject = useCallback((project: SavedProject) => {
    console.log(`[LoadProject] Loading project: ${project.name} (${project.id})`);

    // 0. Activate loading transition overlay with project thumbnail
    setProjectLoadingState({ isLoading: true, thumbnailUrl: project.thumbnailUrl });

    // 1. FIRST: Clean up existing export subscriptions BEFORE changing project
    // 1. FIRST: Clean up existing export subscriptions BEFORE changing project
    resetExportState();

    // 2. Stop playback and reset timeline to 0
    if (timelinePlayerRef.current) {
      timelinePlayerRef.current.pause();
      timelinePlayerRef.current.seek(0);
    }

    // 3. Reset ALL project-specific states COMPLETELY
    setNavigationStack([]); // Reset any nested navigation (Synthé/Panel editing)
    setCachedSourceClips([]); // Clear cached source clips
    setVfxSegmentPreviews([]); // Clear VFX previews
    currentVersionIdStateRef.current = null;
    setCurrentVersionId(null); // Reset active version selection
    setSelectedClipId(null); // Clear clip selection
    setIsClipContextMenuOpen(false);

    // 3.5 Clear clips immediately to reset canvas to black
    runWithoutAutoMontageFork(() => {
      updateClips(() => []);
    });

    const loadedProjectClips = injectPreloadedUrls(project.clips);
    const cacheableRemoteVideos = getRemoteVideoClipsNeedingLocalCache([
      ...loadedProjectClips,
      ...(project.montageVersions?.flatMap(version => version.clips || []) || [])
    ]);

    // 4. Load project data (slight delay to ensure canvas is cleared)
    // Inject preloaded blob URLs so intros/outros/panels play instantly
    setTimeout(() => {
      runWithoutAutoMontageFork(() => {
        updateClips(() => loadedProjectClips);
      });
    }, 50);

    // 4.5 Auto-detect active subtitle language from loaded clips
    const detectSubtitleLang = (clips: any[]) => {
      for (const clip of clips) {
        if (clip.subtitles && typeof clip.subtitles === 'object') {
          const langs = Object.keys(clip.subtitles).filter(k => clip.subtitles[k]?.length > 0);
          if (langs.length > 0) {
            // Priority: fr → en → first available
            if (langs.includes('fr')) return 'fr';
            if (langs.includes('en')) return 'en';
            return langs[0];
          }
        }
      }
      return null;
    };
    const detectedLang = detectSubtitleLang(loadedProjectClips);
    if (detectedLang) {
      console.log(`[LoadProject] Auto-selected subtitle language: ${detectedLang}`);
      setActiveSubtitleLanguage(detectedLang);
    }

    // MIGRATION FIX: Check if tracks need update
    const needsTrackMigration = !project.tracks ||
      project.tracks.some(t => t.id === 'a1' || t.label === 'Principal') ||
      project.tracks.length !== INITIAL_TRACKS.length;

    if (needsTrackMigration) {
      console.log('[LoadProject] Migrating tracks to new schema...');
      // Merge visibility/magnetic settings from old tracks where possible, or just reset
      // For now, reset to ensure clean state as IDs have changed
      setTracks(INITIAL_TRACKS.map(t => ({ ...t })));
    } else {
      setTracks(project.tracks);
    }

    console.log(`[LoadProject] Hydrating montages. Found: ${project.montageVersions?.length || 0}`);
    if (project.montageVersions?.length) {
      console.log('[LoadProject] Versions:', project.montageVersions.map(v => v.name));
    }

    setMontageVersions(project.montageVersions || []);
    montageVersionsRef.current = project.montageVersions || [];
    // Restore to last version if exists
    if (project.montageVersions && project.montageVersions.length > 0) {
      const lastVersion = project.montageVersions[project.montageVersions.length - 1];
      console.log(`[LoadProject] Restoring active version: ${lastVersion.id} (${lastVersion.name})`);
      currentVersionIdStateRef.current = lastVersion.id;
      setCurrentVersionId(lastVersion.id);
    }

    // 5. Load project-specific exports
    if (project.exports && project.exports.length > 0) {
      // Map saved exports to ExportedVideo format
      setExportedVideos(project.exports.map(e => ({
        id: e.id || `export-${Date.now()}`,
        name: e.name,
        urlWebM: e.urlWebM || e.url || '',
        urlMP4: e.urlMP4,
        thumbnailUrl: project.thumbnailUrl || e.thumbnailUrl, // Use project thumbnail if available
      })));
    } else {
      setExportedVideos([]);
    }

    // 6. Load project-specific AI suggestions
    setEditingSuggestions(project.aiSuggestions || []);

    // 6.5 Restore generated voiceovers
    if (project.generatedVoices && project.generatedVoices.length > 0) {
      setGeneratedVoices(project.generatedVoices.map(v => ({
        id: v.id,
        text: v.text,
        voice: v.voice,
        url: v.url,
        duration: v.duration,
        isLoading: false
      })));
    } else {
      setGeneratedVoices([]);
    }
    setVoiceoverText(project.voiceoverText || '');

    // 7. Load project-specific chat history OR show welcome message
    const baseMessages = project.messages && project.messages.length > 0
      ? project.messages.filter(message => message.pendingChoice?.type !== 'local-cache-choice')
      : [{ id: Date.now(), role: 'assistant', text: `Projet "${project.name}" chargé ! Comment puis-je vous aider ?` }];

    setMessages(cacheableRemoteVideos.length > 0 ? [
      ...baseMessages,
      {
        id: `local-cache-choice-${Date.now()}`,
        role: 'assistant',
        text: `Ce projet contient ${cacheableRemoteVideos.length} vidéo(s) distante(s). Voulez-vous créer un cache local pour cette session afin de réduire le lag en preview et en export ?`,
        pendingChoice: {
          type: 'local-cache-choice',
          projectId: project.id,
          options: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]
        }
      }
    ] : baseMessages);

    // 8. NOW set the project ID (after all state is reset)
    setCurrentProjectId(project.id);

    // 8.5 Clear loading transition after a short delay (allow videos to start loading)
    setTimeout(() => {
      setProjectLoadingState({ isLoading: false });
    }, 1200);

    // 9. Check for active export jobs for this project (async, non-blocking)
    checkActiveExportJobs(project.id);

    // 10. ASYNC: Load subtitles from R2 or inline fallback (non-blocking)
    // This runs in the background and updates clips when subtitles are loaded
    (async () => {
      const clipsNeedingSubs = collectProjectClips(project).filter((c: any) =>
        !c.subtitles && (c.subtitlesUrl || c._compactSubtitles)
      );
      if (clipsNeedingSubs.length > 0) {
        console.log(`[LoadProject] Loading ${clipsNeedingSubs.length} subtitle sets...`);
        const hydratedProject = await hydrateProjectSubtitles(project);
        const hydratedClips = collectProjectClips(hydratedProject);
        console.log(`[LoadProject] Subtitles loaded`);

        // Auto-select subtitle language after async hydration
        const langAfterHydration = detectSubtitleLang(hydratedClips);
        if (langAfterHydration) {
          console.log(`[LoadProject] Auto-selected subtitle language after hydration: ${langAfterHydration}`);
          setActiveSubtitleLanguage(langAfterHydration);
        }
      }
    })();


    // Detect missing files
    const missing: string[] = [];
    const checkRecursive = (clips: Clip[]) => {
      clips.forEach(clip => {
        // Missing Assets Detection & Trigger Regeneration
        if (clip.url && !clip.url.startsWith('file-requirement:')) {
          const isVideo = clip.type === 'video';
          const isImage = clip.type === 'image';
          const isAudio = clip.type === 'audio';

          if ((isVideo || isImage) && (!clip.thumbnails || clip.thumbnails.length === 0)) {
            console.log(`[Load] Triggering thumbnail regeneration for ${clip.name}`);
            generateThumbnails(clip.url).then(thumbnails => {
              updateClipSafe(project.id, clip.id, c => ({ ...c, thumbnails }));
            }).catch(e => console.warn(`[Load] Thumbnail regeneration failed for ${clip.name}`, e));
          }

          if (isAudio && (!clip.waveform || clip.waveform.length === 0)) {
            console.log(`[Load] Triggering waveform regeneration for ${clip.name}`);
            generateWaveformData(clip.url).then(waveform => {
              updateClipSafe(project.id, clip.id, c => ({ ...c, waveform }));
            }).catch(e => console.warn(`[Load] Waveform regeneration failed for ${clip.name}`, e));
          }
        }

        if (clip.url && clip.url.startsWith('file-requirement:')) {
          const name = clip.url.replace('file-requirement:', '');
          if (!missing.includes(name)) missing.push(name);
        }
        if (clip.syntheData && clip.syntheData.clips) {
          checkRecursive(clip.syntheData.clips);
        }
      });
    };
    checkRecursive(project.clips);

    if (missing.length > 0) {
      setMissingFiles(missing);
      setIsRelinkModalOpen(true);
    }
  }, [updateClips, setTracks, injectPreloadedUrls, collectProjectClips, hydrateProjectSubtitles]);

  // URL-based project loading: Load project when URL contains projectId
  useEffect(() => {
    if (urlProjectId && urlProjectId !== currentProjectId && user) {
      (async () => {
        console.log(`[URL] Loading project from URL: ${urlProjectId}`);
        // First check local savedProjects (owned projects)
        const localProject = savedProjects.find(p => p.id === urlProjectId);
        if (localProject) {
          loadProject(localProject);
          return;
        }
        // Otherwise fetch from Firestore (shared project)
        const remoteProject = await getProjectById(urlProjectId);
        if (remoteProject) {
          // Open shared project directly (no confirmation needed)
          loadProject(remoteProject);
          setSavedProjects(prev => [...prev, remoteProject]);
        } else {
          setMessages(prev => [...prev, {
            id: Date.now(),
            role: 'assistant',
            text: `⚠️ Projet "${urlProjectId}" introuvable. Il a peut-être été supprimé.`
          }]);
          navigate('/');
        }
      })();
    }
  }, [urlProjectId, currentProjectId, user, savedProjects, loadProject, navigate]);

  // undo/redo provided by hook

  const addMontageVersion = useCallback(async (clips: Clip[], prompt: string, name?: string) => {
    const versionId = `version-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Calculate new versions outside state setter to use in async save
    let newVersions: MontageVersion[] = [];

    setMontageVersions(prev => {
      // Count existing montages (not including Version originale)
      const montageCount = prev.filter(v => v.name.startsWith('Montage')).length;
      const versionName = name || `Montage ${montageCount + 1}`;
      const clonedClips = clips.map(c => ({ ...c }));
      const newVersion = { id: versionId, name: versionName, clips: clonedClips, prompt };

      newVersions = [...prev, newVersion];
      montageVersionsRef.current = newVersions;
      return newVersions;
    });

    currentVersionIdStateRef.current = versionId;
    setCurrentVersionId(versionId);

    // FORCE PERSISTENCE: Save immediately to Firestore
    if (currentProjectId && user) {
      try {
        console.log('[App] Force-saving new montage version...');
        const projectBase = savedProjects.find(p => p.id === currentProjectId);
        if (!projectBase) {
          console.error("[App] Critical: Current project not found in savedProjects cache!");
          return versionId;
        }

        const projectToSave = {
          id: currentProjectId,
          name: projectBase.name || 'Untitled Project',
          clips: allClips,
          tracks: tracks,
          activeVideoVersion: versionId,
          montageVersions: newVersions, // The critical update
          lastModified: Date.now(),
          ownerId: user.uid,
          // preserved fields
          assets: projectBase.assets || [],
          messages: messages,
          editingSuggestions: editingSuggestions,
          exportedVideos: exportedVideos,
          createdAt: projectBase.createdAt || Date.now()
        };

        await saveProjectToFirestore(projectToSave as any);
        console.log('[App] Montage version saved to Firestore successfully.');

        // Update local cache to prevent stale data overwrites
        setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, montageVersions: newVersions } as any : p));

      } catch (err) {
        console.error("Failed to save montage version:", err);
        alert("Erreur de sauvegarde ! Votre montage n'a pas pu être enregistré dans le cloud. Vérifiez votre connexion.");
      }
    }

    return versionId;
  }, [currentProjectId, user, allClips, tracks, savedProjects, messages, editingSuggestions, exportedVideos]);

  const ensureBaseVersion = useCallback((clips: Clip[]) => {
    if (montageVersions.length === 0 && clips.length > 0) {
      addMontageVersion(clips, 'Clips originaux', 'Version originale');
    }
  }, [addMontageVersion, montageVersions.length]);

  const handleSelectVersion = useCallback((versionId: string) => {
    const targetVersion = montageVersions.find(v => v.id === versionId);
    if (!targetVersion) return;

    runWithoutAutoMontageFork(() => {
      currentVersionIdStateRef.current = versionId;
      setCurrentVersionId(versionId);
      updateClips(() => targetVersion.clips.map(c => ({ ...c }))); // Clean switch
    });
  }, [montageVersions, runWithoutAutoMontageFork, updateClips]);


  const generateAndApplyEditPlan = async (userRequest: string, useCurrentMontage: boolean = false, elements: MontageElements = { intro: false, outro: false, synthe: false, panel: false }) => {
    const loadingMessageId = `loading-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: loadingMessageId,
        role: 'assistant',
        text: "",
        isLoading: true,
        statusText: useCurrentMontage ? "Analyse du montage actuel..." : "Analyse des sous-titres...",
        progress: 0,
      },
    ]);

    try {
      // If user selected "original", prefer the base montage clips (which usually keep full transcript data)
      const baseMontage = montageVersions.find(v => v.name === 'Version originale') || montageVersions[0];
      const sourceClips = useCurrentMontage
        ? allClips
        : (baseMontage?.clips?.length ? baseMontage.clips : allClips);

      // Build transcript context from subtitles first, then fallback to stored transcript text URL.
      const transcriptChunks = await Promise.all(sourceClips.map(async (clip) => {
        let analysisLang = activeSubtitleLanguage;
        let clipTranscript = '';
        let transcriptSource: 'subtitles' | 'transcriptUrl' | null = null;

        const hasSubtitleTrack = !!clip.subtitles && Object.keys(clip.subtitles).some(lang => lang !== 'none' && clip.subtitles![lang]?.length > 0);
        if (hasSubtitleTrack) {
          if (analysisLang === 'none' || !clip.subtitles?.[analysisLang]?.length) {
            analysisLang = Object.keys(clip.subtitles || {}).find(l => l !== 'none' && clip.subtitles?.[l]?.length > 0) || 'fr';
          }

          const subtitles = clip.subtitles?.[analysisLang] || [];
          clipTranscript = subtitles
            .map(sub => `[${(sub.start - clip.start).toFixed(1)}s - ${(sub.end - clip.start).toFixed(1)}s] ${sub.text}`)
            .join('\n');
          transcriptSource = 'subtitles';
        }

        if (!clipTranscript && clip.transcriptUrl) {
          try {
            const transcriptResponse = await fetch(clip.transcriptUrl);
            if (transcriptResponse.ok) {
              clipTranscript = (await transcriptResponse.text()).trim();
              transcriptSource = 'transcriptUrl';
            }
          } catch (fetchErr) {
            console.warn(`[Montage] Impossible de charger transcriptUrl pour le clip ${clip.id}:`, fetchErr);
          }
        }

        if (!clipTranscript) return null;

        const safeDuration = Number.isFinite(clip.originalDuration) ? clip.originalDuration : clip.duration;
        const sourceLabel = transcriptSource === 'subtitles' ? `Langue: ${analysisLang}` : 'Source: transcript du projet';
        return `--- DEBUT CLIP (ID: ${clip.id}, Durée: ${safeDuration.toFixed(1)}s, ${sourceLabel}) ---\n${clipTranscript}\n--- FIN CLIP (ID: ${clip.id}) ---`;
      }));

      const transcriptAndSourceInfo = transcriptChunks.filter(Boolean).join('\n\n');

      if (!transcriptAndSourceInfo) {
        setMessages(prev => [
          ...prev.filter(m => m.id !== loadingMessageId),
          { id: Date.now(), role: 'assistant', text: "Je n'ai trouvé ni sous-titres ni transcript exploitable dans ce projet. Vérifie que la transcription a bien été générée puis réessaie." },
        ]);
        return;
      }

      setMessages(prev => prev.map(m =>
        m.id === loadingMessageId
          ? { ...m, statusText: "Génération du plan de montage...", progress: 25 }
          : m
      ));

      const editPlanSchema = {
        type: Type.OBJECT,
        properties: {
          chatResponse: { type: Type.STRING, description: "Réponse libre au format texte (ex: un résumé). Si vous fournissez un résumé ou répondez à une question sans modifier le montage, utilisez uniquement ce champ." },
          newClips: {
            type: Type.ARRAY,
            description: "La nouvelle séquence de clips pour le montage. Doit être vide si textReplacements ou chatResponse est fourni.",
            items: {
              type: Type.OBJECT,
              properties: {
                originalClipId: { type: Type.STRING, description: "L'ID du clip source original." },
                startTime: { type: Type.NUMBER, description: "Timestamp de début du segment dans le clip original (en secondes)." },
                endTime: { type: Type.NUMBER, description: "Timestamp de fin du segment dans le clip original (en secondes)." },
                reasoning: { type: Type.STRING, description: "Brève explication du choix de ce segment." },
              },
              required: ["originalClipId", "startTime", "endTime", "reasoning"]
            }
          },
          newOverlays: {
            type: Type.ARRAY,
            description: "Surcouches (synthés ou panels) à ajouter. Doit être vide si textReplacements ou chatResponse est fourni.",
            items: {
              type: Type.OBJECT,
              properties: {
                type: { type: Type.STRING, enum: ["synthe", "panel"], description: "Le type de surcouche." },
                startTime: { type: Type.NUMBER, description: "Temps de début sur le montage final (en secondes)." },
                duration: { type: Type.NUMBER, description: "Durée de la surcouche (en secondes)." },
                content: {
                  type: Type.OBJECT,
                  properties: {
                    name: { type: Type.STRING, description: "Pour les synthés: le nom de la personne." },
                    title: { type: Type.STRING, description: "Pour les synthés: le titre/poste de la personne." },
                    question: { type: Type.STRING, description: "Pour les panels: la question à afficher." }
                  }
                },
                reasoning: { type: Type.STRING, description: "Brève explication de l'ajout." }
              },
              required: ["type", "startTime", "duration", "content", "reasoning"]
            }
          },
          textReplacements: {
            type: Type.ARRAY,
            description: "Liste des corrections textuelles à appliquer (orthographe, noms, etc.). Si fourni, ne pas toucher aux clips.",
            items: {
              type: Type.OBJECT,
              properties: {
                targetText: { type: Type.STRING, description: "Le texte exact ou partiel à remplacer (ex: 'ecric')." },
                replacementText: { type: Type.STRING, description: "Le texte de remplacement (ex: 'Eric')." },
                scope: { type: Type.STRING, enum: ["all", "subtitles", "overlays"], description: "Où appliquer le changement." }
              },
              required: ["targetText", "replacementText", "scope"]
            }
          }
        }
      };

      // Build elements instruction based on toggles
      const enabledElements: string[] = [];
      const disabledElements: string[] = [];
      if (elements.intro) enabledElements.push('Intro'); else disabledElements.push('Intro');
      if (elements.outro) enabledElements.push('Outro'); else disabledElements.push('Outro');
      if (elements.synthe) enabledElements.push('Synthé (identification des intervenants)'); else disabledElements.push('Synthé');
      if (elements.panel) enabledElements.push('Panel (écrans titres/questions)'); else disabledElements.push('Panel');

      const elementsInstruction = disabledElements.length > 0
        ? `\n7. **Éléments à NE PAS inclure :** ${disabledElements.join(', ')}. Ne génère AUCUN de ces éléments même si pertinents.`
        : '';

      const payload = {
        model: 'gemini-3.5-flash',
        contents: [{
          role: 'user',
          parts: [{
            text: `Tu es un monteur vidéo expert. Ta mission est de créer un nouveau montage OU de corriger du texte, en suivant la demande de l'utilisateur.

**Demande utilisateur :** "${userRequest}"

**Clips & Transcriptions disponibles :**
${transcriptAndSourceInfo}

**Instructions :**

**CAS 1 : CORRECTION DE TEXTE / ORTHOGRAPHE**
Si l'utilisateur demande de corriger des fautes, des noms ou de remplacer du texte (ex: "Remplace X par Y", "Corrige le nom de Z") :
1. Remplis UNIQUEMENT le champ \`textReplacements\`.
2. Laisse \`newClips\` vide (tableau vide []).
3. Ne propose PAS de nouveaux clips ou overlays. On ne touche pas au montage.

**CAS 2 : NOUVEAU MONTAGE**
Si l'utilisateur demande de créer une vidéo, un résumé, ou de modifier le contenu visuel :
1. Crée un plan de montage qui répond à la demande de l'utilisateur en utilisant des segments des clips fournis.
2. **Priorité à la fluidité :** Ne coupe JAMAIS une phrase en plein milieu. Ajuste les timestamps \`startTime\` et \`endTime\` pour qu'ils correspondent au début et à la fin de phrases complètes.
3. Les segments seront assemblés dans l'ordre que tu définis.
4. Les timestamps (\`startTime\`, \`endTime\`) doivent être en secondes et relatifs au **début du clip original**.
5. **Surcouches (Synthés & Panels) :** Tu peux suggérer l'ajout de synthés (bas d'écran avec nom/titre) ou de panels (fond noir avec question). 
   - Utilise un **synthé** lorsqu'une nouvelle personne parle pour l'identifier (ex: "Richy", "Expert IA").
   - Utilise un **panel** pour introduire une nouvelle section ou une question clé.
   - Les \`startTime\` des surcouches sont relatifs au **début du montage final** (le résultat de l'assemblage des clips).${elementsInstruction}
   - N'ajoute jamais de champ \`overlays\` a l'interieur de \`newClips\`. Utilise uniquement \`newOverlays\`.
**CAS 3 : RÉPONSE LIBRE / RÉSUMÉ**
Si l'utilisateur pose une question, demande un résumé ou des conseils :
1. Remplis UNIQUEMENT le champ \`chatResponse\` avec ta réponse détaillée.
2. Laisse \`newClips\` et \`textReplacements\` vides ou absents.

6. Ta réponse DOIT être un unique objet JSON respectant un seul des 3 cas.
   - Si montage: "newClips" rempli.
   - Si correction texte: "textReplacements" rempli.
   - Si réponse libre: "chatResponse" rempli.`}]
        }],
        config: {
          responseMimeType: "application/json",
          responseSchema: editPlanSchema,
        }
      };

      // FIX: Explicitly type HttpsCallableResult to resolve type inference issue.
      const geminiResponse: firebase.functions.HttpsCallableResult = await callGemini(payload);

      setMessages(prev => prev.map(m =>
        m.id === loadingMessageId
          ? { ...m, statusText: "Analyse terminée. Application...", progress: 50 }
          : m
      ));
      // FIX: Add a specific interface for the parsed JSON to aid TypeScript's type inference and prevent errors.
      interface GeminiEditingPlan {
        chatResponse?: string;
        newClips?: { originalClipId: string, startTime: number, endTime: number, reasoning: string }[];
        newOverlays?: {
          type: 'synthe' | 'panel',
          startTime: number,
          duration: number,
          content: { name?: string, title?: string, question?: string },
          reasoning: string
        }[];
        textReplacements?: {
          targetText: string;
          replacementText: string;
          scope: 'all' | 'subtitles' | 'overlays';
        }[];
      }

      const responseData = geminiResponse.data as any;
      let jsonString = "{}";

      if (responseData.candidates && responseData.candidates[0]?.content?.parts?.[0]?.text) {
        jsonString = responseData.candidates[0].content.parts[0].text;
      } else if (responseData.text) {
        jsonString = responseData.text;
      } else if (typeof responseData === 'string') {
        jsonString = responseData;
      } else {
        console.warn("[App] Unexpected Gemini response format:", responseData);
        throw new Error("Format de réponse IA invalide.");
      }

      const parsedPlan = parseAiJsonResponse<GeminiEditingPlan>(jsonString, { recoverEditingPlan: true });
      console.log("[MontagePlan] Parsed with strategy:", parsedPlan.strategy);
      console.log("[MontagePlan] Cleaned JSON (first 200 chars):", parsedPlan.cleanJson.substring(0, 200));
      const result = parsedPlan.data;

      // === CAS 3: RÉPONSE LIBRE ===
      if (result.chatResponse) {
        setMessages(prev => [
          ...prev.filter(m => m.id !== loadingMessageId),
          { id: Date.now(), role: 'assistant', text: result.chatResponse }
        ]);
        return;
      }

      // === HANDLING TEXT REPLACEMENTS ===
      if (result.textReplacements && result.textReplacements.length > 0) {
        setMessages(prev => prev.map(m => m.id === loadingMessageId ? { ...m, statusText: "Application des corrections de texte...", progress: 75 } : m));

        const replacements = result.textReplacements;
        let totalReplacementsCount = 0;

        const updatedClips = allClips.map(clip => {
          let updatedClip = { ...clip };
          let changed = false;

          // 1. Subtitles
          if (updatedClip.subtitles) {
            const newSubtitles = { ...updatedClip.subtitles };
            Object.keys(newSubtitles).forEach(lang => {
              if (newSubtitles[lang]) {
                newSubtitles[lang] = newSubtitles[lang].map(sub => {
                  let newText = sub.text;
                  replacements.forEach(rep => {
                    if (rep.scope === 'all' || rep.scope === 'subtitles') {
                      // Case insensitive replacement
                      const regex = new RegExp(rep.targetText, 'gi');
                      if (regex.test(newText)) {
                        newText = newText.replace(regex, rep.replacementText);
                        totalReplacementsCount++;
                        changed = true;
                      }
                    }
                  });
                  return { ...sub, text: newText };
                });
              }
            });
            if (changed) updatedClip.subtitles = newSubtitles;
          }

          // 2. Synthe / Overlays (Text clips, or Synthe components)
          // Direct text property (Text clip, Synthe main title often stored in name or text)
          if (updatedClip.text) {
            replacements.forEach(rep => {
              if (rep.scope === 'all' || rep.scope === 'overlays') {
                const regex = new RegExp(rep.targetText, 'gi');
                if (regex.test(updatedClip.text!)) {
                  updatedClip.text = updatedClip.text!.replace(regex, rep.replacementText);
                  totalReplacementsCount++;
                  changed = true;
                }
              }
            });
          }

          // Synthe Data Components
          if (updatedClip.syntheData && updatedClip.syntheData.clips) {
            const newSyntheClips = updatedClip.syntheData.clips.map(subClip => {
              let newSubClip = { ...subClip };
              if (newSubClip.text) {
                replacements.forEach(rep => {
                  if (rep.scope === 'all' || rep.scope === 'overlays') {
                    const regex = new RegExp(rep.targetText, 'gi');
                    if (regex.test(newSubClip.text!)) {
                      newSubClip.text = newSubClip.text!.replace(regex, rep.replacementText);
                      totalReplacementsCount++;
                      changed = true;
                    }
                  }
                });
              }
              return newSubClip;
            });
            updatedClip.syntheData = { ...updatedClip.syntheData, clips: newSyntheClips };
          }

          return updatedClip;
        });

        if (totalReplacementsCount > 0) {
          updateClips(updatedClips);
          setMessages(prev => [
            ...prev.filter(m => m.id !== loadingMessageId),
            {
              id: Date.now(),
              role: 'assistant',
              text: `✅ C'est corrigé ! J'ai effectué ${totalReplacementsCount} remplacements ("${replacements[0].targetText}" → "${replacements[0].replacementText}"...) dans les sous-titres et synthés.`
            },
          ]);
        } else {
          setMessages(prev => [
            ...prev.filter(m => m.id !== loadingMessageId),
            { id: Date.now(), role: 'assistant', text: "Je n'ai pas trouvé le texte à remplacer. Vérifiez l'orthographe exacte." },
          ]);
        }
        return; // STOP HERE, DO NOT PROCEED TO MONTAGE
      }

      // === HANDLING MONTAGE ===
      if (!result.newClips || !Array.isArray(result.newClips)) {
        throw new Error("La réponse de l'IA n'a pas le format attendu.");
      }

      const editingPlan = result.newClips;

      if (editingPlan.length === 0) {
        setMessages(prev => [
          ...prev.filter(m => m.id !== loadingMessageId),
          { id: Date.now(), role: 'assistant', text: "Je n'ai pas pu trouver de segments pertinents pour créer un montage. Essayez une autre instruction." },
        ]);
        return;
      }

      const originalClipsMap = new Map(sourceClips.map(c => [c.id, c]));

      ensureBaseVersion(allClips);

      const newTimelineClips: Clip[] = [];
      let cumulativeTime = 0;
      const totalSegments = editingPlan.length;

      for (const [index, segment] of editingPlan.entries()) {
        // Normalize clip ID field — Replicate model may return any of these field names
        const segmentClipId = String(segment.originalClipId || (segment as any).clipId || (segment as any).clip_id || '').trim();
        let originalClipUntyped = originalClipsMap.get(segmentClipId);
        if (!originalClipUntyped && segmentClipId) {
          originalClipUntyped = sourceClips.find(clip =>
            clip.id === segmentClipId ||
            segmentClipId.includes(clip.id) ||
            clip.id.includes(segmentClipId)
          );
        }
        if (!originalClipUntyped && sourceClips.length === 1) {
          originalClipUntyped = sourceClips[0];
        }
        if (!originalClipUntyped) {
          console.warn(`Clip original avec l'ID ${segmentClipId} non trouvé. Segment ignoré. (keys tried: originalClipId, clipId, clip_id)`);
          continue;
        }
        // FIX: Assert the type of 'originalClip' to resolve a chain of type inference errors.
        const originalClip = originalClipUntyped as Clip;

        const safeSegmentStart = Math.max(0, Math.min(originalClip.duration, Number(segment.startTime)));
        const safeSegmentEnd = Math.max(safeSegmentStart, Math.min(originalClip.duration, Number(segment.endTime)));
        const segmentDuration = safeSegmentEnd - safeSegmentStart;
        if (segmentDuration <= 0) {
          console.warn(`Durée de segment invalide pour le clip ${segmentClipId}. Segment ignoré.`);
          continue;
        }

        let newClipSubtitles: Record<string, Subtitle[]> | undefined = undefined;
        if (originalClip.subtitles) {
          newClipSubtitles = {};

          const segmentTimelineRelativeStart = safeSegmentStart - originalClip.trimStart;
          const segmentOriginalAbsoluteStart = originalClip.start + segmentTimelineRelativeStart;
          const segmentOriginalAbsoluteEnd = originalClip.start + (safeSegmentEnd - originalClip.trimStart);

          for (const [lang, originalLangSubs] of Object.entries(originalClip.subtitles)) {
            if (originalLangSubs && Array.isArray(originalLangSubs)) {
              const relevantSubs = originalLangSubs.filter((sub: Subtitle) =>
                sub.start < segmentOriginalAbsoluteEnd && sub.end > segmentOriginalAbsoluteStart
              );

              newClipSubtitles[lang] = relevantSubs.map((sub: Subtitle) => {
                const trimmedSubStart = Math.max(segmentOriginalAbsoluteStart, sub.start);
                const trimmedSubEnd = Math.min(segmentOriginalAbsoluteEnd, sub.end);
                const timeShift = cumulativeTime - segmentOriginalAbsoluteStart;

                return {
                  ...sub,
                  start: trimmedSubStart + timeShift,
                  end: trimmedSubEnd + timeShift
                };
              }).filter(sub => sub.end > sub.start);
            }
          }
        }

        const newClip = createNewClip({
          ...originalClip,
          id: `montage-cut-${Date.now()}-${Math.random()}`,
          // Video clips go to v2, panels will be on v1 to avoid overlap
          trackId: 'v2',
          start: cumulativeTime,
          trimStart: originalClip.trimStart + safeSegmentStart,
          duration: segmentDuration,
          subtitles: newClipSubtitles,
        });
        newTimelineClips.push(newClip);
        cumulativeTime += segmentDuration;

        const progressPercentage = 50 + Math.round(((index + 1) / totalSegments) * 50);
        setMessages(prev => prev.map(m =>
          m.id === loadingMessageId
            ? { ...m, progress: progressPercentage, statusText: `Application du segment ${index + 1}/${totalSegments}...` }
            : m
        ));
        if (totalSegments > 5) {
          await new Promise(resolve => setTimeout(resolve, 30));
        }
      }

      if (newTimelineClips.length === 0) {
        throw new Error("Le plan de montage n'a produit aucun clip exploitable.");
      }

      // Appliquer les surcouches si présentes (filtered by element toggles)
      if (result.newOverlays && result.newOverlays.length > 0) {
        for (const overlay of result.newOverlays) {
          // Skip synthe if disabled
          if (overlay.type === 'synthe' && !elements.synthe) continue;
          // Skip panel if disabled
          if (overlay.type === 'panel' && !elements.panel) continue;

          if (overlay.type === 'synthe') {
            const syntheClips = [...DEFAULT_SYNTHE_CLIPS];
            // Update names if provided
            if (overlay.content.name) syntheClips[0] = { ...syntheClips[0], text: overlay.content.name };
            if (overlay.content.title) syntheClips[1] = { ...syntheClips[1], text: overlay.content.title };

            const defaultClipsWithUniqueIds = syntheClips.map((clip, idx) => ({
              ...clip,
              id: `synthe-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
            }));

            const newClip = createNewClip({
              type: 'synthe',
              name: `Synthé: ${overlay.content.name || 'Identité'}`,
              start: overlay.startTime,
              duration: overlay.duration || 5,
              trackId: 't1',
              syntheData: { clips: defaultClipsWithUniqueIds, tracks: INITIAL_TRACKS }
            });
            newTimelineClips.push(newClip);
          } else if (overlay.type === 'panel') {
            const panelClips = [...DEFAULT_PANEL_CLIPS];
            // Find text clip to update (usually index 1)
            const textClipIndex = panelClips.findIndex(c => c.type === 'text');
            if (textClipIndex !== -1 && overlay.content.question) {
              panelClips[textClipIndex] = { ...panelClips[textClipIndex], text: overlay.content.question };
            }

            const defaultPanelClipsWithUniqueIds = panelClips.map((clip, idx) => ({
              ...clip,
              id: `panel-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
            }));

            const newClip = createNewClip({
              type: 'panel',
              name: `Panel: ${overlay.content.question || 'Question'}`,
              start: overlay.startTime,
              duration: overlay.duration || 5,
              trackId: 't1',
              syntheData: { clips: defaultPanelClipsWithUniqueIds, tracks: INITIAL_TRACKS, backgroundColor: '#000000' }
            });
            newTimelineClips.push(newClip);
          }
        }
      }

      // Add Intro/Outro if enabled
      const introOutroCategory = DEFAULT_LIBRARY.find(cat => cat.id === 'intro-outro');
      const introItem = introOutroCategory?.items.find((i: any) => i.clipRole === 'intro');
      const outroItem = introOutroCategory?.items.find((i: any) => i.clipRole === 'outro');

      // Add intro: shift all clips by intro duration and add intro at 0
      if (elements.intro && introItem?.url) {
        const introShiftDuration = 2; // Fixed 2-second push for intro (same as LibraryTab)

        // Fetch actual media duration
        let introDuration = 4; // Default
        try {
          introDuration = await getMediaDurationFromUrl(introItem.url);
        } catch (e) {
          console.warn('[Montage] Failed to get intro duration, using default', e);
        }

        // Shift all existing clips
        newTimelineClips.forEach(clip => {
          clip.start += introShiftDuration;
          // Shift subtitles too
          if (clip.subtitles) {
            Object.keys(clip.subtitles).forEach(lang => {
              clip.subtitles![lang] = clip.subtitles![lang].map(sub => ({
                ...sub,
                start: sub.start + introShiftDuration,
                end: sub.end + introShiftDuration,
              }));
            });
          }
        });

        // Add intro clip at start
        const introClip = createNewClip({
          name: introItem.name || 'Intro',
          url: introItem.url,
          type: 'video',
          start: 0,
          duration: introDuration,
          originalDuration: introDuration,
          trackId: 'v1', // Use v1 for intro (on top)
          clipRole: 'intro',
        });
        newTimelineClips.unshift(introClip);
      }

      // Add outro: place at end of timeline
      if (elements.outro && outroItem?.url) {
        const timelineEnd = newTimelineClips.reduce((max, c) => Math.max(max, c.start + c.duration), 0);

        let outroDuration = 4; // Default
        try {
          outroDuration = await getMediaDurationFromUrl(outroItem.url);
        } catch (e) {
          console.warn('[Montage] Failed to get outro duration, using default', e);
        }

        const outroStartTime = Math.max(0, timelineEnd - 1.5); // Overlap by 1.5s (same as LibraryTab)

        const outroClip = createNewClip({
          name: outroItem.name || 'Outro',
          url: outroItem.url,
          type: 'video',
          start: outroStartTime,
          duration: outroDuration,
          originalDuration: outroDuration,
          trackId: 'v1', // Use v1 for outro (on top)
          clipRole: 'outro',
        });
        newTimelineClips.push(outroClip);
      }

      const cleanTimeline = newTimelineClips.map(c => ({ ...c }));

      // Add new montage version (Base version is handled by ensureBaseVersion at start)
      await addMontageVersion(cleanTimeline, userRequest);
      setNavigationStack([]);
      runWithoutAutoMontageFork(() => {
        updateClips(() => cleanTimeline);
      });

      setMessages(prev => [
        ...prev.filter(m => m.id !== loadingMessageId),
        {
          id: Date.now(),
          role: 'assistant',
          text: "Voilà ! J'ai créé un nouveau montage en découpant le clip original sur sa piste. Les parties non sélectionnées ont été retirées.",
          action: {
            label: "Voir le début du montage",
            onClick: () => {
              if (timelinePlayerRef.current) {
                // Seek to 0
                timelinePlayerRef.current.seek(0);
                // Zoom to fit content
                (timelinePlayerRef.current as any).zoomToFit?.();
                // Start playing
                setTimeout(() => {
                  (timelinePlayerRef.current as any).play?.();
                }, 100);
              }
            }
          }
        },
      ]);

    } catch (error) {
      console.error("Erreur lors de la génération du plan de montage:", error);
      setMessages(prev => [
        ...prev.filter(m => m.id !== loadingMessageId),
        {
          id: Date.now(),
          role: 'assistant',
          text: "Désolé, je n'ai pas pu générer de nouveau montage. Une erreur est survenue.",
          action: {
            label: "Réessayer",
            onClick: () => generateAndApplyEditPlan(userRequest)
          }
        },
      ]);
    }
  };

  const handleSendMessage = async (text: string, elements: MontageElements) => {
    // Store elements for use in edit plan
    setCurrentMontageElements(elements);
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', text }]);

    // If a montage already exists, ask the user whether to modify current or start fresh
    if (montageVersions.length > 0) {
      setMessages(prev => [...prev, {
        id: `choice-${Date.now()}`,
        role: 'assistant',
        text: "Voulez-vous modifier le montage actuel ou repartir de la vidéo originale ?",
        pendingChoice: {
          type: 'edit-base-choice',
          originalRequest: text,
          options: [
            { label: "Modifier le montage actuel", value: 'current' },
            { label: "Repartir de la vidéo de base", value: 'original' }
          ]
        }
      }]);
      return;
    }

    // No existing montage, proceed directly with original clips
    await generateAndApplyEditPlan(text, false, elements);
  };

  const handleEditChoice = async (choice: 'current' | 'original' | 'yes' | 'no', pendingChoice: ChatPendingChoice) => {
    setMessages(prev => prev.filter(m => {
      if (!m.pendingChoice) return true;
      if (m.pendingChoice.type !== pendingChoice.type) return true;

      if (pendingChoice.type === 'edit-base-choice') {
        return m.pendingChoice.originalRequest !== pendingChoice.originalRequest;
      }

      return m.pendingChoice.projectId !== pendingChoice.projectId;
    }));

    if (pendingChoice.type === 'local-cache-choice') {
      if (choice === 'yes') {
        await hydrateProjectLocalCache(pendingChoice.projectId);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          text: "OK, on garde les sources distantes pour cette session."
        }]);
      }
      return;
    }

    const useCurrentMontage = choice === 'current';
    await generateAndApplyEditPlan(pendingChoice.originalRequest, useCurrentMontage, currentMontageElements);
  };


  // ============================================================================
  // EXPORT HANDLERS - All moved to hooks/useExportManager.ts
  // isTimelineEligibleForFastExport, handleStartExport, handleCancelExport,
  // uploadAssetToStorage, handleStartServerExport, handleCancelServerExport,
  // handleDeleteExport, checkActiveExportJobs
  // ============================================================================

  const handleQuickCombine = async () => {
    // Check if there are video clips to combine (exclude ONLY intro/outro)
    const videoClips = allClips.filter(c =>
      c.type === 'video' &&
      c.url &&
      c.clipRole !== 'intro' &&
      c.clipRole !== 'outro'
    );
    if (videoClips.length === 0) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        text: "⚠️ Aucun clip vidéo à combiner. Ajoutez des clips vidéo à la timeline pour utiliser cette fonctionnalité."
      }]);
      return;
    }

    if (videoClips.length === 1) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        text: "⚠️ Il n'y a qu'un seul clip vidéo. Ajoutez plus de clips pour utiliser la combinaison."
      }]);
      return;
    }

    // Sort video clips by their timeline position
    const sortedVideoClips = [...videoClips].sort((a, b) => a.start - b.start);
    const targetTrackId = sortedVideoClips[0].trackId;
    const startPosition = Math.min(...sortedVideoClips.map(c => c.start));

    // Get all non-video clips
    const nonVideoClips = allClips.filter(c =>
      c.type !== 'video' || c.clipRole === 'intro' || c.clipRole === 'outro'
    );

    // Add loading message
    const loadingMessageId = `combine-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: loadingMessageId,
      role: 'assistant',
      text: '',
      isLoading: true,
      statusText: `Combinaison de ${videoClips.length} clips via Replicate...`,
      progress: 10
    }]);

    try {
      // Extract video segments with trim info
      const segments = sortedVideoClips.map(clip => ({
        url: clip.url,
        trimStart: clip.trimStart || 0,
        duration: clip.duration
      }));

      setMessages(prev => prev.map(m =>
        m.id === loadingMessageId
          ? { ...m, statusText: 'Envoi à Replicate (FFmpeg)...', progress: 20 }
          : m
      ));

      // Call Cloud Function exportVideo
      const result = await exportVideo({ segments });
      const { videoUrl } = result.data as { videoUrl: string; replicateUrl: string };

      setMessages(prev => prev.map(m =>
        m.id === loadingMessageId
          ? { ...m, statusText: 'Création du clip combiné...', progress: 80 }
          : m
      ));

      // Get duration of combined video
      const combinedDuration = await getMediaDurationFromUrl(videoUrl);

      setMessages(prev => prev.map(m =>
        m.id === loadingMessageId
          ? { ...m, statusText: 'Génération des miniatures...', progress: 90 }
          : m
      ));

      // Generate thumbnails
      const thumbnails = await generateThumbnails(videoUrl);

      // Create new combined clip
      const combinedClip = createNewClip({
        type: 'video',
        name: `Clip Combiné ${new Date().toLocaleTimeString()}`,
        url: videoUrl,
        trackId: targetTrackId,
        start: startPosition,
        duration: combinedDuration,
        originalDuration: combinedDuration,
        trimStart: 0,
        thumbnails,
      });

      // Replace all video clips with the combined clip
      const finalClipsList = [...nonVideoClips, combinedClip];
      updateClips(() => finalClipsList);

      // Success message
      setMessages(prev => [
        ...prev.filter(m => m.id !== loadingMessageId),
        {
          id: Date.now(),
          role: 'assistant',
          text: `✅ ${videoClips.length} clips combinés en un seul fichier ! Le montage a été remplacé. Vidéo stockée sur R2.`
        }
      ]);

    } catch (error) {
      console.error('QuickCombine error:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'Une erreur est survenue lors de la combinaison';

      setMessages(prev => [
        ...prev.filter(m => m.id !== loadingMessageId),
        {
          id: Date.now(),
          role: 'assistant',
          text: `❌ Erreur: ${errorMessage}`
        }
      ]);
    }
  };

  const handleOpenExportMenu = () => {
    setActiveTab('export');
  };

  const generateEditingSuggestions = useCallback(async (subtitles: Subtitle[]) => {
    try {
      // Helper pour formater les timestamps
      const formatTimestamp = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      };

      // Créer une transcription avec timestamps
      const transcriptWithTimestamps = subtitles.map((sub, i) =>
        `[${formatTimestamp(sub.start)} - ${formatTimestamp(sub.end)}] ${sub.text}`
      ).join('\n');

      const payload = {
        model: 'gemini-3.5-flash',
        contents: [{
          role: 'user',
          parts: [
            {
              text: `Tu es un expert en montage vidéo LVMH spécialisé dans l'extraction de segments pour les réseaux sociaux.

TRANSCRIPTION AVEC TIMESTAMPS:
${transcriptWithTimestamps}

OBJECTIF: Analyser le contenu et proposer des SEGMENTS À COUPER de la vidéo originale.

TYPES DE SUGGESTIONS:

1. **SUJETS ISOLÉS** (2-4 suggestions)
   - Identifier des sujets/thèmes complets et auto-suffisants
   - Exemple: Introduction de la maison, explication d'un savoir-faire, présentation produit
   - Chaque segment doit raconter une histoire complète

2. **MONTAGES COURTS** (2-3 suggestions)
   - Extraits percutants pour réseaux sociaux (15-60s)
   - Highlight d'un moment clé
   - Citation marquante
   - Démonstration visuelle

3. **FORMATS SPÉCIFIQUES** (1-2 suggestions)
   - LinkedIn: segment professionnel/corporate
   - TikTok/Reels: moment viral/captivant
   - Teaser: meilleurs extraits en 30s

4. **SURCOUCHES (Synthés)** (1-2 suggestions)
   - Suggérer l'ajout d'un synthé pour identifier une personne qui parle
   - NE JAMAIS suggérer de 'panel' sauf si l'utilisateur le demande explicitement dans sa requête.

RÈGLES STRICTES:
- Pour CHAQUE suggestion, fournis les timestamps PRÉCIS (début et fin en secondes)
- Le "label" doit être court et descriptif (ex: "Savoir-faire artisan", "Citation CEO")
- L'"instruction" doit expliquer QUOI couper et POURQUOI (basé sur le contenu réel)
- Assure-toi que chaque segment a un sens complet (pas de coupure en plein milieu d'une phrase)

FORMAT JSON ATTENDU:
{
  "suggestions": [
    {
      "label": "Titre court du segment",
      "instruction": "Description de ce qui doit être coupé et pourquoi c'est intéressant",
      "startTime": 12.5,
      "endTime": 45.8,
      "duration": 33.3,
      "type": "sujet|court|format|overlay"
    }
  ]
}

IMPORTANT: Base tes suggestions sur le CONTENU RÉEL de la transcription, pas sur des hypothèses.`}
          ]
        }],
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              suggestions: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    label: { type: Type.STRING },
                    instruction: { type: Type.STRING },
                    startTime: { type: Type.NUMBER },
                    endTime: { type: Type.NUMBER },
                    duration: { type: Type.NUMBER },
                    type: { type: Type.STRING, enum: ["sujet", "court", "format", "overlay"] }
                  },
                  required: ["label", "instruction", "startTime", "endTime", "type"]
                }
              }
            },
            required: ["suggestions"],
          },
        }
      };

      const geminiResponse: firebase.functions.HttpsCallableResult = await callGemini(payload);

      // Robust response extraction (handle various Gemini response formats)
      const responseData = geminiResponse.data as any;
      console.log("[EditingSuggestions] Raw response data keys:", Object.keys(responseData || {}));
      console.log("[EditingSuggestions] responseData.text:", typeof responseData?.text, responseData?.text?.substring?.(0, 100));
      console.log("[EditingSuggestions] responseData.candidates:", !!responseData?.candidates);

      let jsonString = "";
      if (responseData.candidates && responseData.candidates[0]?.content?.parts?.[0]?.text) {
        jsonString = responseData.candidates[0].content.parts[0].text;
      } else if (responseData.text) {
        jsonString = responseData.text;
      } else if (typeof responseData === 'string') {
        jsonString = responseData;
      } else {
        console.warn("[EditingSuggestions] Unexpected Gemini response format:", JSON.stringify(responseData).substring(0, 500));
        throw new Error("Format de réponse IA invalide.");
      }

      console.log("[EditingSuggestions] Extracted jsonString (first 200 chars):", jsonString.substring(0, 200));

      const parsedSuggestions = parseAiJsonResponse<{ suggestions?: EditingSuggestion[] }>(jsonString);
      console.log("[EditingSuggestions] Parsed with strategy:", parsedSuggestions.strategy);
      console.log("[EditingSuggestions] Cleaned JSON (first 200 chars):", parsedSuggestions.cleanJson.substring(0, 200));

      const result = parsedSuggestions.data;
      if (result.suggestions && Array.isArray(result.suggestions)) {
        setEditingSuggestions(result.suggestions);
        setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: "J'ai analysé la vidéo ! Voici quelques suggestions de montage." }]);
      }
    } catch (error: any) {
      console.error("Editing suggestion generation error:", error);
      const errorMessage = error?.message || error?.toString() || "";
      if (errorMessage.includes("temporarily unavailable") || errorMessage.includes("500") || errorMessage.includes("E004")) {
        setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: "L'intelligence artificielle est temporairement indisponible (serveurs saturés). Veuillez réessayer dans quelques instants." }]);
      } else {
        setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: "Je n'ai pas pu générer de suggestions de montage pour cette vidéo." }]);
      }
    }
  }, [setMessages, setEditingSuggestions]);

  /**
   * Extrait l'audio en utilisant FFmpeg (fallback method)
   * ✨ Optimisé: Sortie MP3 64k Mono pour upload rapide
   */


  /**
   * Traduction manuelle des sous-titres pour une langue spécifique
   */
  const handleTranslateSubtitles = async (targetLang: string, silent: boolean = false, explicitClip?: Clip) => {
    console.log(`[Translate] handleTranslateSubtitles called with: ${targetLang}, silent: ${silent}`);
    console.log(`[Translate] translationsInProgress:`, Array.from(translationsInProgress));

    // Vérifier si une traduction est déjà en cours pour cette langue
    if (translationsInProgress.has(targetLang)) {
      console.warn(`[Translate] Translation to ${targetLang} already in progress`);
      alert(`Une traduction vers ${targetLang.toUpperCase()} est déjà en cours. Veuillez patienter.`);
      return;
    }

    // Vérifier si une transcription est en cours
    const isTranscribing = messages.some(m => m.isLoading && m.statusText?.toLowerCase().includes('transcription'));
    console.log(`[Translate] isTranscribing: ${isTranscribing}`);

    if (isTranscribing) {
      alert('Veuillez attendre que la transcription soit terminée avant de lancer la traduction.');
      return;
    }

    // Trouver le clip avec des sous-titres (basé sur explicitClip, selectedClipId ou le premier clip avec des sous-titres)
    // On doit s'assurer que l'objet subtitles n'est pas vide (il doit y avoir au moins une langue avec des textes)
    const hasValidSubtitles = (c?: Clip) =>
      !!c?.subtitles &&
      Object.keys(c.subtitles).length > 0 &&
      Object.values(c.subtitles).some((arr: any) => arr && arr.length > 0);

    const findClipWithSubtitles = (clips: Clip[]) => {
      if (explicitClip) {
        if (hasValidSubtitles(explicitClip)) return explicitClip;
        const explicitMatch = clips.find(c => c.id === explicitClip.id && hasValidSubtitles(c));
        if (explicitMatch) return explicitMatch;
      }

      if (selectedClipId) {
        const selectedMatch = clips.find(c => c.id === selectedClipId && hasValidSubtitles(c));
        if (selectedMatch) return selectedMatch;
      }

      return clips.find(hasValidSubtitles);
    };

    let subtitleSearchClips = allClips;
    let clipWithSubtitles = findClipWithSubtitles(subtitleSearchClips);

    if (!clipWithSubtitles && currentProjectId) {
      const projectSnapshot = savedProjects.find(project => project.id === currentProjectId);
      if (projectSnapshot) {
        try {
          const hydratedProject = await hydrateProjectSubtitles(projectSnapshot);
          subtitleSearchClips = collectProjectClips(hydratedProject);
          clipWithSubtitles = findClipWithSubtitles(subtitleSearchClips);
          console.log(`[Translate] clipWithSubtitles after hydration: ${clipWithSubtitles?.id}`);
        } catch (error) {
          console.warn('[Translate] Failed to hydrate subtitles before translation:', error);
        }
      }
    }

    console.log(`[Translate] clipWithSubtitles found: ${clipWithSubtitles?.id}`);

    if (!clipWithSubtitles || !clipWithSubtitles.subtitles) {
      console.error(`[Translate] No subtitles found to translate.`);
      alert('Aucun sous-titre disponible à traduire. Veuillez d\'abord générer la transcription.');
      return;
    }

    // ... (rest of function remains same until updateClips) ...

    // Trouver la langue source (première langue avec des sous-titres)
    const availableLanguages = Object.keys(clipWithSubtitles.subtitles);
    if (availableLanguages.length === 0) {
      alert('Aucun sous-titre disponible à traduire.');
      return;
    }

    // Vérifier si la traduction existe déjà
    if (clipWithSubtitles.subtitles[targetLang] && clipWithSubtitles.subtitles[targetLang].length > 0) {
      if (!silent) {
        const confirmReTranslate = window.confirm(`Une traduction en ${targetLang.toUpperCase()} existe déjà. Voulez-vous la retraduire ?`);
        if (!confirmReTranslate) return;
      }
    }

    // Marquer la traduction comme en cours
    setTranslationsInProgress(prev => new Set(prev).add(targetLang));

    let isCancelled = false;
    let globalFirestoreUnsubscribe: (() => void) | null = null;

    const sourceLang = availableLanguages[0];

    // Aggregate full subtitles for translation so we don't just translate one cut part
    const sameVideoClips = subtitleSearchClips.filter(c => c.type === 'video' && c.url === clipWithSubtitles.url);
    const allSourceSubsMap = new Map<string | number, Subtitle>();
    for (const c of sameVideoClips) {
      if (c.subtitles && c.subtitles[sourceLang]) {
        for (const sub of c.subtitles[sourceLang]) {
          if (allSourceSubsMap.has(sub.id)) {
            const existing = allSourceSubsMap.get(sub.id)!;
            existing.start = Math.min(existing.start, sub.start);
            existing.end = Math.max(existing.end, sub.end);
            if (sub.text.length > existing.text.length) {
              existing.text = sub.text;
            }
          } else {
            allSourceSubsMap.set(sub.id, { ...sub });
          }
        }
      }
    }
    const sourceSubtitles = Array.from(allSourceSubsMap.values()).sort((a, b) => a.start - b.start);

    const translationMessageId = `translation-${targetLang}-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: translationMessageId,
      role: 'assistant',
      text: '',
      isLoading: true,
      statusText: `Traduction vers ${targetLang.toUpperCase()} en cours...`,
      progress: 0,
      onCancel: () => {
        isCancelled = true;
        globalFirestoreUnsubscribe?.();
        setMessages(msgs => msgs.map(m => m.id === translationMessageId ? {
          ...m,
          isLoading: false,
          statusText: 'Annulée',
          text: `❌ Traduction vers ${targetLang.toUpperCase()} annulée.`
        } : m));
        setTranslationsInProgress(curr => {
          const next = new Set(curr);
          next.delete(targetLang);
          return next;
        });
      }
    }]);

    try {
      // OPTIMIZATION: Send all subtitles at once (Backend timeout increased to 540s)
      setMessages(prev => prev.map(m => m.id === translationMessageId
        ? {
          ...m,
          statusText: `Traduction vers ${targetLang.toUpperCase()} en cours (traitement complet)...`,
          progress: 10
        }
        : m
      ));

      const translationJobId = `translation_${targetLang}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

      // Helper: poll Firestore for translation results (survives HTTP timeouts)
      const pollFirestoreForTranslation = async (): Promise<any> => {
        const { firestore } = await import('./firebase');

        return new Promise((resolve, reject) => {
          globalFirestoreUnsubscribe = firestore.collection('translation-jobs').doc(translationJobId).onSnapshot((snapshot: any) => {
            if (isCancelled) {
              globalFirestoreUnsubscribe?.();
              return;
            }
            if (!snapshot.exists) return;
            const data = snapshot.data();

            if (data?.status === 'complete') {
              globalFirestoreUnsubscribe?.();
              resolve({ data: { subtitles: data.subtitles, language: targetLang } });
            } else if (data?.status === 'error') {
              globalFirestoreUnsubscribe?.();
              reject(new Error(data.error || 'Translation failed'));
            } else if (data?.status === 'processing') {
              // Optional: Update granular progress if available
              if (data.progress) {
                setMessages(prev => prev.map(m => m.id === translationMessageId
                  ? { ...m, progress: data.progress } : m
                ));
              }
            }
          });
        });
      };

      if (sourceSubtitles.length === 0) {
        throw new Error("Aucun sous-titre à traduire.");
      }

      // Fire-and-forget: send HTTP call ONCE (no retry), poll Firestore immediately
      // Whichever returns first wins — avoids duplicate jobs
      const httpPromise = translateSubtitlesBatch({
        subtitles: sourceSubtitles,
        sourceLang,
        targetLang,
        batchStart: 0,
        jobId: translationJobId
      }).catch((err: any) => {
        console.warn('[Translate] HTTP call timed out or failed, waiting for Firestore...', err);
        return null; // Don't throw, let Firestore win
      });

      const result = await Promise.race([
        httpPromise.then(r => r ? r : pollFirestoreForTranslation()),
        pollFirestoreForTranslation()
      ]);

      if (isCancelled) return;

      const resultData = result.data as { subtitles: Subtitle[] };
      const translatedSubtitles = resultData.subtitles || [];

      if (translatedSubtitles.length === 0) {
        throw new Error("La traduction n'a renvoyé aucun résultat.");
      } setMessages(prev => prev.map(m => m.id === translationMessageId
        ? {
          ...m,
          statusText: `Application des traductions...`,
          progress: 90
        }
        : m
      ));

      // Ajouter les IDs et les offsets de timeline
      let translatedWithIds = translatedSubtitles.map((sub: Subtitle, index: number) => ({
        ...sub,
        id: index,
      }));

      // Fix any overlapping subtitle timings
      translatedWithIds = fixSubtitleOverlaps(translatedWithIds) as typeof translatedWithIds;

      console.log(`[Translate] Translation to ${targetLang} finished with ${translatedWithIds.length} subtitles.`);
      console.log(`[Translate] Target Clip URL: "${clipWithSubtitles.url}" (ID: ${clipWithSubtitles.id})`);

      // Mettre à jour les clips : cible prioritaire l'ID sélectionné, puis fallback sur URL
      // Cela garantit que le clip visible est mis à jour même si l'URL a légèrement changé.
      updateClips(prev => {
        const next = prev.map(c => {
          // On met à jour si c'est LE clip sélectionné (par ID) ou un autre clip partageant la même vidéo (par URL)
          // La comparaison stricte URL peut échouer, l'ID est la source de vérité pour le clip actif
          const isSelectedClip = c.id === clipWithSubtitles.id;
          const isSameVideo = c.url && clipWithSubtitles.url && c.url === clipWithSubtitles.url;

          if (isSelectedClip || (isSameVideo && c.type === 'video')) {
            console.log(`[Translate] Updating subtitles for clip ${c.id} (Match ID: ${isSelectedClip}, Match URL: ${isSameVideo})`);
            // Filter the full translated subtitles to fit into this specific clip's boundaries
            const translatedForClip = filterSubtitlesByRange([...translatedWithIds], c.start, c.start + c.duration);
            return { ...c, subtitles: { ...(c.subtitles || {}), [targetLang]: translatedForClip } };
          }
          return c;
        });

        const updatedCount = next.filter(c => c.subtitles?.[targetLang]).length;
        console.log(`[Translate] Clips update commit. Total clips available with ${targetLang}: ${updatedCount}`);

        // Check if the update actually changed anything
        if (updatedCount === 0) {
          console.warn(`[Translate] WARNING: No clips were updated! Check strict URL equality or ID.`);
          prev.forEach(c => {
            if (c.type === 'video') console.log(`[Translate] Candidate: "${c.url}" (ID Match: ${c.id === clipWithSubtitles.id}, URL Match: ${c.url === clipWithSubtitles.url})`);
          });
        }

        return next;
      }, false);
      setMessages(prev => prev.map(m => m.id === translationMessageId ? {
        ...m,
        isLoading: false,
        text: `✅ Traduction vers ${targetLang.toUpperCase()} terminée.`,
        progress: 100
      } : m));

      // Changer la langue active pour afficher la nouvelle traduction
      setActiveSubtitleLanguage(targetLang);

      // Retirer la traduction de la liste des traductions en cours
      setTranslationsInProgress(prev => {
        const next = new Set(prev);
        next.delete(targetLang);
        return next;
      });

    } catch (err) {
      console.error("La traduction a échoué:", err);
      setMessages(prev => prev.map(m => m.id === translationMessageId ? {
        ...m,
        isLoading: false,
        text: `⚠️ La traduction vers ${targetLang.toUpperCase()} a échoué. Veuillez réessayer.`,
      } : m));

      // Retirer la traduction de la liste des traductions en cours même en cas d'erreur
      setTranslationsInProgress(prev => {
        const next = new Set(prev);
        next.delete(targetLang);
        return next;
      });
    }
  };

  const generateSubtitlesForClip = async (
    clip: Clip,
    mediaBlob: Blob | File,
    projectId: string,
    options?: {
      logFn?: (msg: string) => void,
      onProgress?: (progress: number, status: string) => void
    }
  ) => {
    // If no external logging provided, create a local message state (Legacy behavior)
    let loadingMessageId: string | null = null;
    let isCancelled = false;
    let globalFirestoreUnsubscribe: (() => void) | null = null;

    if (!options?.onProgress) {
      loadingMessageId = `loading-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: loadingMessageId!, role: "assistant", text: "", isLoading: true, statusText: "Préparation du fichier...", progress: 0,
        onCancel: () => {
          isCancelled = true;
          globalFirestoreUnsubscribe?.();
          setMessages(msgs => msgs.map(m => m.id === loadingMessageId ? {
            ...m,
            isLoading: false,
            statusText: 'Annulée',
            text: `❌ Transcription de "${clip.name}" annulée.`
          } : m));
        }
      }]);
    }

    updateClipSafe(projectId, clip.id, c => ({ ...c, isProcessingSubtitles: true }));

    const updateStatus = (statusText: string, progress: number) => {
      if (options?.onProgress) {
        options.onProgress(progress, statusText);
      } else if (loadingMessageId) {
        setMessages(prev => prev.map(m => m.id === loadingMessageId ? { ...m, statusText, progress } : m));
      }
    };

    const log = (msg: string) => {
      if (options?.logFn) options.logFn(msg);
      else console.log(`[Subtitles] ${msg}`);
    };

    const finalizeProgress = (statusText: string) => {
      if (options?.onProgress) {
        options.onProgress(100, statusText);
      }
    };

    try {
      // 🚀 PIPELINE: Extract MP3 + Transcribe + Upload video ALL IN PARALLEL
      let audioUrl: string | null = null;
      let videoUrl = clip.url;
      let mp3TranscriptionSuccess = false;

      // 📹 Start video upload in BACKGROUND (runs concurrently with extraction + transcription)
      let videoUploadPromise: Promise<string | null> | null = null;
      if (!videoUrl || videoUrl.startsWith('blob:')) {
        const fileName = (mediaBlob instanceof File) ? mediaBlob.name : `video_${Date.now()}.mp4`;
        log(`🚀 Upload vidéo lancé en parallèle: ${fileName}`);
        updateClipSafe(projectId, clip.id, c => ({ ...c, isUploading: true }));

        videoUploadPromise = uploadFileToR2({
          file: mediaBlob,
          type: 'video',
          logFn: log,
          onProgress: (p) => {
            // Only show upload progress if transcription is already done or failed
            if (mp3TranscriptionSuccess) {
              updateStatus(`📤 Upload vidéo... ${p}%`, 92 + Math.round(p * 0.08));
            }
          }
        }).then(publicUrl => {
          log(`✅ Upload vidéo terminé: ${publicUrl}`);
          updateClipSafe(projectId, clip.id, c => {
            const existingSources = c.audioSources || [];
            const withoutOriginal = existingSources.filter(s => s.type !== 'original');
            const localUrl = getClipLocalSourceUrl(c);
            return {
              ...c,
              url: publicUrl,
              ...(localUrl ? { localUrl } : {}),
              isUploading: false,
              audioSources: [{ id: 'original', type: 'original', url: publicUrl }, ...withoutOriginal],
              activeAudioSource: c.activeAudioSource || 'original'
            };
          });
          return publicUrl;
        }).catch(err => {
          console.error("Upload video failed", err);
          updateClipSafe(projectId, clip.id, c => ({ ...c, isUploading: false }));
          return null;
        });
      }

      // 🎧 PRIMARY PATH: Client-side MP3 extraction + Replicate Whisper/Gemini Flash
      // Much faster than server-side ingestion: small MP3 upload (~15MB) vs full video (~500MB+)
      try {
        // STEP A: Extract MP3 client-side using FFmpeg.wasm
        updateStatus('🎧 Extraction audio MP3...', 5);
        console.log('[Transcription] Extracting MP3 client-side...');

        const mp3Blob = await FFmpegService.extractAudioFromLargeFile(
          mediaBlob instanceof File ? mediaBlob : new File([mediaBlob], clip.name || 'video.mp4', { type: mediaBlob.type || 'video/mp4' }),
          (ratio) => {
            updateStatus(`🎧 Extraction audio... ${Math.round(ratio * 100)}%`, 5 + Math.round(ratio * 15));
          }
        );
        const mp3File = new File([mp3Blob], `audio_${Date.now()}.mp3`, { type: 'audio/mpeg' });
        console.log(`[Transcription] MP3 extracted: ${(mp3File.size / 1024 / 1024).toFixed(1)} MB`);

        // STEP B: Upload MP3 to R2 (same robust path as Constance: simple/multipart + retries)
        updateStatus('📤 Upload audio...', 22);
        const mp3PublicUrl = await uploadFileToR2({
          file: mp3File,
          type: 'audio',
          logFn: log,
          onProgress: (p) => {
            updateStatus(`📤 Upload audio... ${p}%`, 22 + Math.round(p * 0.08));
          }
        });
        console.log(`[Transcription] MP3 uploaded: ${mp3PublicUrl}`);

        // STEP C: Transcribe via Replicate Whisper + Gemini Flash (LVMH lexicon)
        updateStatus('🧠 Transcription Whisper + Gemini Flash...', 30);

        // Generate unique jobId for Firestore recovery (survives HTTP drops)
        const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

        // Helper: poll Firestore for transcription results (with cleanup support)
        let firestoreUnsubscribe: (() => void) | null = null;
        let raceResolved = false;

        const pollFirestoreForResult = async (): Promise<any> => {
          const { firestore } = await import('./firebase');
          return new Promise((resolve, reject) => {
            globalFirestoreUnsubscribe = firestore.collection('transcription-jobs').doc(transcriptionJobId).onSnapshot((snapshot: any) => {
              if (isCancelled) { globalFirestoreUnsubscribe?.(); return; }
              if (raceResolved) { globalFirestoreUnsubscribe?.(); return; }
              if (!snapshot.exists) return;
              const data = snapshot.data();
              if (data?.status === 'complete') {
                globalFirestoreUnsubscribe?.();
                resolve({ data: { subtitles: data.subtitles, language: data.language } });
              } else if (data?.status === 'error') {
                globalFirestoreUnsubscribe?.();
                reject(new Error(data.error || 'Transcription failed'));
              } else if (data?.status === 'processing') {
                const serverProgress = data.progress || 55;
                const serverStep = data.currentStep || '⏳ Transcription en cours sur le serveur (Large File)...';
                updateStatus(serverStep, serverProgress);
              }
            });
          });
        };

        // Fire-and-forget: send HTTP call ONCE, poll Firestore immediately
        // Whichever returns first wins (Constance strategy).
        const httpPromise = transcribeAudioConstance({
          audioUrl: mp3PublicUrl,
          // Ne pas forcer 'en' sinon toutes les vidéos de l'éditeur seront traduites en anglais sans demander !
          response_format: 'verbose_json',
          jobId: transcriptionJobId
        }).catch((err: any) => {
          console.log('[Transcription] HTTP call dropped (normal for long files):', err.message);
          return null;
        });

        const transRes = await Promise.race([
          httpPromise.then(r => r ? r : pollFirestoreForResult()),
          pollFirestoreForResult()
        ]);

        if (isCancelled) return;

        // Cleanup: mark race as done and unsubscribe any lingering Firestore listener
        raceResolved = true;
        globalFirestoreUnsubscribe?.();

        const data = (transRes.data as any);
        console.log('[Transcription] Replicate response:', data);

        // Flexible parsing (handle various Replicate/Whisper output formats)
        let transcriptText = data.text || data.transcription || (data.output && data.output.text) || '';
        const segments = data.subtitles || data.segments;

        if (segments && segments.length > 0) {
          // Utiliser la langue détectée ou transmise par l'API, avec fallback
          const rawLang = data.language || 'en';
          const sourceLang = rawLang.substring(0, 2).toLowerCase();

          let subtitlesWithIds = segments.map((seg: any, index: number) => ({
            id: index, // <--- FIXED REGRESSION: SubtitlesTab expects id to be a number. String "en-0" broke maxId math (NaN).
            text: seg.text,
            start: seg.start,
            end: seg.end
          }));
          subtitlesWithIds = fixSubtitleOverlaps(subtitlesWithIds);

          updateClipSafe(projectId, clip.id, c => ({
            ...c,
            subtitles: { [sourceLang]: subtitlesWithIds }
          }));

          setActiveSubtitleLanguage(sourceLang);
          setMessages(prev => prev.filter(m => m.id !== loadingMessageId));

          if (clip.type === 'video') {
            try {
              await generateEditingSuggestions(subtitlesWithIds);
            } catch (e) { console.warn('Editing suggestions failed', e); }
          }

          mp3TranscriptionSuccess = true;
          finalizeProgress('✅ Transcription terminée');
          console.log(`[Transcription] ✅ Success: ${subtitlesWithIds.length} subtitles via Replicate`);
        } else if (transcriptText) {
          console.warn('[Transcription] Got text but no segments, falling back');
        } else {
          console.warn('[Transcription] Empty response, falling back');
        }
      } catch (mp3Error) {
        console.error('[Transcription] MP3 extraction or Replicate failed, falling back:', mp3Error);
      }

      // If MP3 transcription succeeded, we still want to trigger Cloudflare Stream upload
      // for optimal HLS playback, even though we already have subtitles.
      if (mp3TranscriptionSuccess) {
        if (videoUploadPromise) {
          updateStatus('Finalisation de l\'upload R2...', 90);
          const uploadedUrl = await videoUploadPromise;
          if (uploadedUrl) videoUrl = uploadedUrl;
        }

        if (videoUrl && !videoUrl.startsWith('blob:')) {
          updateStatus('Préparation du flux vidéo optimisé (HLS)...', 95);
          try {
            const exportClient = getServerExportClient(auth.currentUser?.uid || 'anonymous');
            const streamResult = await exportClient.uploadToStream(
              videoUrl,
              clip.id,
              (status) => updateStatus(status, 95)
            );

            if (streamResult.success && streamResult.streamUrl) {
              const streamUrl = streamResult.streamUrl;
              // Cloudflare Stream manifests do not reliably expose CORS for browser-side HEAD polling.
              // We persist the stream URL immediately and let playback/export paths wait for readiness server-side.
              console.log('[Stream] Associating HLS stream URL to clip:', streamUrl);
              updateClipSafe(projectId, clip.id, c => ({
                ...c,
                streamUrl: streamUrl
              }));
            }
          } catch (streamError) {
            console.warn('[Stream] Background Stream upload failed, staying with raw MP4:', streamError);
          }
        }

        finalizeProgress('✅ Transcription et Streaming terminés');
        return;
      }

      // MP3 transcription failed: wait for video upload before fallback ingestion/transcription.
      if (videoUploadPromise) {
        const uploadedUrl = await videoUploadPromise;
        if (uploadedUrl) videoUrl = uploadedUrl;
      }

      // FALLBACK 1: Server-side ingestion (if MP3 path failed and we have a video URL)
      if (videoUrl && !videoUrl.startsWith('blob:')) {
        console.log('[Ingest] Falling back to server-side ingestion for:', videoUrl);
        updateStatus('Ingestion vidéo (fallback)...', 42);

        try {
          const exportClient = getServerExportClient(auth.currentUser?.uid || 'anonymous');
          const ingestionResult = await exportClient.triggerIngestion(
            videoUrl,
            projectId,
            clip.id,
            (status, progress) => {
              updateStatus(status, 42 + Math.round(progress * 0.5));
            }
          );

          if (ingestionResult.success && ingestionResult.mergedTranscript) {
            const sourceLang = ingestionResult.language ? ingestionResult.language.substring(0, 2) : 'fr';
            const subtitlesWithIds = ingestionResult.mergedTranscript.map((sub, index) => ({
              ...sub,
              id: `${sourceLang}-${index}`
            }));

            updateClipSafe(projectId, clip.id, c => ({
              ...c,
              subtitles: { [sourceLang]: subtitlesWithIds },
              chunks: ingestionResult.chunks?.map(chunk => ({
                streamUrl: chunk.streamUrl,
                startTime: chunk.startTime,
                duration: chunk.duration
              }))
            }));

            setActiveSubtitleLanguage(sourceLang);
            setMessages(prev => prev.filter(m => m.id !== loadingMessageId));

            if (clip.type === 'video') {
              try {
                await generateEditingSuggestions(subtitlesWithIds);
              } catch (e) { console.warn('Editing suggestions failed', e); }
            }

            finalizeProgress('✅ Transcription terminée');
            return; // Done via ingestion fallback
          } else {
            console.warn('[Ingestion] Failed, falling to legacy transcription:', ingestionResult.error);
          }
        } catch (ingestionError) {
          console.error('[Ingestion] Error, falling to legacy:', ingestionError);
        }
      }

      const transcribeUrl = audioUrl || videoUrl;
      updateStatus(audioUrl ? "Transcription audio..." : "Extraction et transcription serveur...", 45);

      const transcriptionJobId = `transcription_app_${Date.now()}_${Math.random().toString(36).substring(7)}`;

      // Helper: poll Firestore for transcription results
      const pollFirestoreForTranscription = async (): Promise<any> => {
        const { firestore } = await import('./firebase');

        return new Promise((resolve, reject) => {
          globalFirestoreUnsubscribe = firestore.collection('transcription-jobs').doc(transcriptionJobId).onSnapshot((snapshot: any) => {
            if (isCancelled) { globalFirestoreUnsubscribe?.(); return; } // Added cancellation check
            if (!snapshot.exists) return;
            const data = snapshot.data();

            if (data?.status === 'complete') {
              globalFirestoreUnsubscribe?.();
              resolve({ data: { subtitles: data.subtitles, language: data.language } });
            } else if (data?.status === 'error') {
              globalFirestoreUnsubscribe?.();
              reject(new Error(data.error || 'Transcription failed'));
            } else if (data?.status === 'processing') {
              if (data.progress) {
                updateStatus(data.currentStep || '⏳ Transcription en cours sur le serveur...', data.progress);
              }
            }
          });
        });
      };

      // Fire-and-forget: send HTTP call ONCE (no retry), poll Firestore immediately
      const httpPromise = transcribeAudio({
        audioUrl: transcribeUrl,
        isLargeFile: true,
        useGpt4oTranscribe,
        jobId: transcriptionJobId
      }).catch((err: any) => {
        console.warn('[Transcription] HTTP call timed out/failed, waiting for Firestore...', err);
        return null;
      });

      const transcriptionResult = await Promise.race([
        httpPromise.then(r => r ? r : pollFirestoreForTranscription()),
        pollFirestoreForTranscription()
      ]);

      const { subtitles: sourceSubtitles, language } = (transcriptionResult.data as any);

      updateStatus(`Formatage des sous-titres...`, 90);
      let sourceLang = (language || 'en').substring(0, 2).toLowerCase();
      // On autorise la langue retournée par Whisper, fallback sur 'fr' uniquement si c'est vraiment non détecté 
      // Si la vidéo est en anglais, ça mettra 'en', si français 'fr'
      if (!['fr', 'en', 'es', 'de', 'it', 'pt', 'nl'].includes(sourceLang)) sourceLang = 'fr';

      // IMPL-FIX: Store subtitles with SOURCE-RELATIVE timestamps (0-based to the file).
      // This prevents desync when moving/trimming the clip later.
      // Also apply auto-fix for overlaps.
      let subtitlesWithTimelineOffset = sourceSubtitles.map((sub: Subtitle, index: number) => ({
        ...sub, id: `${sourceLang}-${index}`, start: sub.start, end: sub.end
      }));
      subtitlesWithTimelineOffset = fixSubtitleOverlaps(subtitlesWithTimelineOffset);

      updateClipSafe(projectId, clip.id, c => ({ ...c, subtitles: { [sourceLang]: subtitlesWithTimelineOffset } }));
      setActiveSubtitleLanguage(sourceLang);

      setMessages(prev => prev.filter(m => m.id !== loadingMessageId));

      if (clip.type === 'video') {
        // Suggestions de montage seulement (pas de traduction auto)
        try {
          await generateEditingSuggestions(sourceSubtitles);
        } catch (e) { console.warn("Editing suggestions failed", e); }
      }

      finalizeProgress('✅ Transcription terminée');

    } catch (error) {
      console.error("Erreur lors de la génération des sous-titres :", error);
      finalizeProgress('❌ Erreur de transcription');
      setMessages(prev => [...prev.filter(m => m.id !== loadingMessageId), {
        id: Date.now(), role: 'assistant', text: `Erreur : ${error instanceof Error ? error.message : 'Inconnue'}`
      }]);
    } finally {
      updateClipSafe(projectId, clip.id, c => ({ ...c, isProcessingSubtitles: false }));
    }
  };

  const handleTimelineFileDrop = async (
    file: File | Blob | null,
    clip: Clip,
    projectId?: string,
    options?: {
      logFn?: (msg: string) => void,
      onProgress?: (progress: number, status: string) => void
    }
  ) => {
    const targetProject = projectId || currentProjectId;
    if (!targetProject) {
      console.error("No project ID available for file drop");
      return;
    }

    // Video upload is now handled INSIDE generateSubtitlesForClip (after MP3 extraction + transcription)
    // No need for a separate early upload here.

    // 2. Determine Media Blob for processing
    let mediaBlob: Blob;
    if (file && file instanceof Blob) {
      mediaBlob = file;
    } else if (clip.url) {
      try {
        const response = await fetch(clip.url);
        mediaBlob = await response.blob();
      } catch (e) {
        console.error('[Subtitles] Failed to fetch from clip URL:', e);
        return;
      }
    } else {
      console.error('[Subtitles] No blob or URL available for clip');
      return;
    }

    // 3. Generate Subtitles (Videos only)
    if (clip.type === 'video') {
      await generateSubtitlesForClip(clip, mediaBlob, targetProject, options);
    }
  };

  const handleGenerateSubtitles = async (clip: Clip) => {
    if (!clip.url || clip.isProcessingSubtitles) return;

    updateClipSafe(currentProjectId!, clip.id, c => ({ ...c, isProcessingSubtitles: true }));

    let firestoreUnsubscribe: (() => void) | null = null;
    let isCancelled = false;
    const loadingMessageId = Date.now();
    setMessages(prev => [...prev, {
      id: loadingMessageId,
      role: 'assistant',
      text: `⏳ Transcription de "${clip.name}" en cours...`,
      isLoading: true,
      onCancel: () => {
        isCancelled = true;
        firestoreUnsubscribe?.();
        setMessages(msgs => msgs.map(m => m.id === loadingMessageId ? {
          ...m,
          isLoading: false,
          statusText: 'Annulée',
          text: `❌ Transcription de "${clip.name}" annulée par l'utilisateur.`
        } : m));
      }
    }]);

    try {
      if (isCancelled) return;

      const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

      const pollFirestoreForResult = async (): Promise<any> => {
        const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
        const db = getFirestore();
        const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);
        return new Promise((resolve, reject) => {
          firestoreUnsubscribe = onSnapshot(jobRef, (snapshot) => {
            if (isCancelled) {
              firestoreUnsubscribe?.();
              resolve(null); // Resolve with null to indicate cancellation
              return;
            }
            if (!snapshot.exists()) return;
            const data = snapshot.data();
            if (data?.status === 'complete') {
              firestoreUnsubscribe?.();
              resolve({ data: { subtitles: data.subtitles, language: data.language } });
            } else if (data?.status === 'error') {
              firestoreUnsubscribe?.();
              reject(new Error(data.error || 'Transcription failed'));
            } else if (data?.status === 'processing') {
              const serverProgress = data.progress || 50;
              const serverStep = data.currentStep || '⏳ Transcription en cours...';
              setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
                ...m,
                statusText: `${serverStep} (${serverProgress}%)`,
                progress: serverProgress
              } : m));
            }
          });
        });
      };

      const httpPromise = transcribeAudioConstance({
        audioUrl: clip.url,
        isLargeFile: true,
        useGpt4oTranscribe: false,
        jobId: transcriptionJobId
        // Pas de language="en" forcé ici non plus
      }).catch((err: any) => {
        console.log('[GenerateSubtitles] HTTP call dropped:', err.message);
        return null; // fallback to polling
      });

      const transRes = await Promise.race([
        httpPromise.then((r: any) => r ? r : pollFirestoreForResult()),
        pollFirestoreForResult()
      ]);

      if (isCancelled) return; // Exit if cancelled after race

      const data = transRes.data as any;
      const segments = data.subtitles || data.segments;

      if (segments && segments.length > 0) {
        const rawLang = data.language || 'en';
        const sourceLang = rawLang.substring(0, 2).toLowerCase();

        let subtitlesWithIds = segments.map((seg: any, index: number) => ({
          id: index, // <--- FIXED REGRESSION: Keep numeric ID
          text: seg.text,
          start: seg.start,
          end: seg.end
        }));
        subtitlesWithIds = fixSubtitleOverlaps(subtitlesWithIds);

        updateClipSafe(currentProjectId!, clip.id, c => ({
          ...c,
          subtitles: { [sourceLang]: subtitlesWithIds }
        }));
        setActiveSubtitleLanguage(sourceLang);

        setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
          ...m,
          isLoading: false,
          statusText: 'Terminé',
          progress: 100,
          text: `✅ Transcription terminée pour "${clip.name}" (${subtitlesWithIds.length} sous-titres générés)`
        } : m));
      } else {
        throw new Error("Aucun sous-titre généré");
      }
    } catch (err: any) {
      if (isCancelled) return; // Do not show error if cancelled
      firestoreUnsubscribe?.();
      setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
        ...m,
        isLoading: false,
        statusText: 'Erreur',
        text: `❌ Erreur de transcription: ${err.message}`
      } : m));
    } finally {
      updateClipSafe(currentProjectId!, clip.id, c => ({ ...c, isProcessingSubtitles: false }));
    }
  };



  const enhanceClipAudio = async (clip: Clip) => {
    try {
      // Marquer le clip comme en cours d'amélioration
      updateClips(prev => prev.map(c =>
        c.id === clip.id ? { ...c, isEnhancingAudio: true } : c
      ), false);

      // Vérifier que le clip a une URL
      if (!clip.url) {
        throw new Error("Le clip n'a pas d'URL source");
      }

      // Récupérer l'audio du clip
      const response = await fetch(clip.url);
      const mediaBlob = await response.blob();
      const arrayBuffer = await mediaBlob.arrayBuffer();

      // Décoder l'audio
      const audioCtx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

      // LIMIT: Max 30 seconds for audio enhancement (like video is 5s max)
      const MAX_AUDIO_DURATION = 30;
      const durationToProcess = Math.min(clip.duration, MAX_AUDIO_DURATION);
      const startOffset = clip.trimStart || 0;

      console.log(`[Audio Enhance] Original: ${audioBuffer.duration.toFixed(1)}s, Processing: ${durationToProcess.toFixed(1)}s from ${startOffset.toFixed(1)}s`);

      // Convertir en stéréo 48kHz pour une HAUTE QUALITÉ (pas de compression transcription)
      const targetSampleRate = 48000;
      const targetChannels = 2; // Stéréo
      const OfflineAudioContext = (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;

      // Create context for LIMITED duration only
      const offlineCtx = new OfflineAudioContext(targetChannels, durationToProcess * targetSampleRate, targetSampleRate);
      const source = offlineCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineCtx.destination);
      // Start from offset and play only the limited duration
      source.start(0, startOffset, durationToProcess);

      const resampledBuffer = await offlineCtx.startRendering();

      // Récupérer les deux canaux (stéréo)
      const leftChannel = resampledBuffer.getChannelData(0);
      const rightChannel = resampledBuffer.numberOfChannels > 1
        ? resampledBuffer.getChannelData(1)
        : resampledBuffer.getChannelData(0); // Si mono, dupliquer

      // Interleave les canaux
      const interleavedData = new Float32Array(leftChannel.length + rightChannel.length);
      for (let i = 0; i < leftChannel.length; i++) {
        interleavedData[i * 2] = leftChannel[i];
        interleavedData[i * 2 + 1] = rightChannel[i];
      }

      // Encoder en WAV stéréo
      const pcmInt16Data = floatTo16BitPCM(interleavedData);
      const audioWavBlob = encodeWav(pcmInt16Data, targetSampleRate, targetChannels);

      // Créer URL pour l'audio original
      const originalUrl = URL.createObjectURL(audioWavBlob);

      // Upload vers Firebase
      const uploadConfig: firebase.functions.HttpsCallableResult = await getUploadUrl({
        fileName: `clip-${clip.id}-original.wav`,
        contentType: 'audio/wav'
      });
      const { uploadUrl, gcsUri } = (uploadConfig.data as any);
      await fetch(uploadUrl, { method: 'PUT', body: audioWavBlob, headers: { 'Content-Type': 'audio/wav' } });

      // Appeler la Cloud Function pour améliorer l'audio
      const enhanceResult = await enhanceAudio({ gcsUri });
      const { enhancedUrl, enhancedGcsUri } = (enhanceResult.data as any);

      // Convert WAV → MP3 client-side via ffmpeg.wasm for better browser compat & smaller size
      let finalEnhancedUrl = enhancedUrl;
      try {
        console.log('[EnhanceAudio] Converting WAV → MP3 via ffmpeg.wasm...');
        const mp3Blob = await FFmpegService.convertWavToMp3(enhancedUrl);

        // Upload MP3 to R2
        const r2Upload = await getR2UploadUrl({
          fileName: `enhanced-audio/${clip.id}-enhanced.mp3`,
          contentType: 'audio/mpeg'
        });
        const { uploadUrl: r2UploadUrl, publicUrl: r2PublicUrl } = (r2Upload.data as any);
        await fetch(r2UploadUrl, {
          method: 'PUT',
          body: mp3Blob,
          headers: { 'Content-Type': 'audio/mpeg' }
        });
        finalEnhancedUrl = r2PublicUrl;
        console.log('[EnhanceAudio] ✅ MP3 uploaded to R2:', finalEnhancedUrl);
      } catch (convErr) {
        console.warn('[EnhanceAudio] WAV→MP3 conversion failed, using WAV fallback:', convErr);
        // Keep original WAV URL as fallback
      }

      // Mettre à jour le clip avec les sources audio et activer l'audio amélioré par défaut
      updateClips(prev => prev.map(c => {
        if (c.id === clip.id) {
          const newSourceId = `enhanced-${Date.now()}`;
          // Ensure audioSources is initialized and migrated
          const currentSources = c.audioSources || [];
          let safeSources = [...currentSources];

          // Ensure original exists if missing (legacy migration)
          if (safeSources.length === 0 && c.url) {
            safeSources.push({ id: 'original', type: 'original', url: c.url, label: 'Original' });
          }

          const newSources = [...safeSources, {
            id: newSourceId,
            type: 'enhanced',
            url: finalEnhancedUrl,
            gcsUri: enhancedGcsUri,
            label: `Amélioré (${new Date().toLocaleTimeString()})`,
            createdAt: Date.now()
          } as AudioSource];

          return {
            ...c,
            audioSources: newSources,
            activeAudioSource: newSourceId,
            isEnhancingAudio: false
          };
        }
        return c;
      }), false); // N'enregistre pas dans l'historique immédiatement
    } catch (error) {
      console.error("Erreur d'amélioration audio:", error);
      updateClips(prev => prev.map(c =>
        c.id === clip.id ? { ...c, isEnhancingAudio: false } : c
      ), false);
      alert("L'amélioration audio a échoué.");
    }
  };

  const handleSelectAudioSource = (clip: Clip, sourceId: string) => {
    updateClips(prev => prev.map(c => {
      if (c.id === clip.id) {
        const updated = { ...c, activeAudioSource: sourceId };
        // Si c'est un clip audio pur, on met à jour l'URL principale pour la timeline
        if (c.type === 'audio' && c.audioSources) {
          const source = c.audioSources.find(s => (s.id || s.type) === sourceId);
          if (source) updated.url = source.url;
        }
        return updated;
      }
      return c;
    }), false);
  };

  const handleDeleteAudioSource = (clip: Clip, sourceId: string) => {
    updateClips(prev => prev.map(c => {
      if (c.id === clip.id && c.audioSources) {
        const newSources = c.audioSources.filter(s => (s.id || s.type) !== sourceId);

        let newActive = c.activeAudioSource;
        // If we deleted the active source, fallback to another one
        if ((c.activeAudioSource === sourceId) || (!c.activeAudioSource && sourceId === 'enhanced')) {
          // Prefer last enhanced, then original
          const lastEnhanced = newSources.filter(s => s.type === 'enhanced').pop();
          const original = newSources.find(s => s.type === 'original');
          newActive = lastEnhanced ? (lastEnhanced.id || lastEnhanced.type) : (original ? (original.id || original.type) : undefined);
        }

        const updated = { ...c, audioSources: newSources, activeAudioSource: newActive };

        // Update main URL if audio clip is affected
        if (c.type === 'audio' && newActive) {
          const source = newSources.find(s => (s.id || s.type) === newActive);
          if (source) updated.url = source.url;
        }
        return updated;
      }
      return c;
    }), false);
  };

  const enhanceClipVideo = async (clip: Clip, prompt: string) => {
    const isEyeContact = prompt === 'EYE_CONTACT';

    try {
      // 1. Marquer comme en cours
      updateClips(prev => prev.map(c =>
        c.id === clip.id ? {
          ...c,
          isEnhancingVideo: !isEyeContact,
          isApplyingEyeContact: isEyeContact
        } : c
      ), false);

      // 2. Si c'est la première amélioration, sauvegarder l'original comme version "v0"
      let currentVersions = clip.videoVersions || [];
      if (currentVersions.length === 0 && clip.url) {
        currentVersions = [{
          id: 'original',
          url: clip.url,
          prompt: 'Original',
          createdAt: Date.now()
        }];
      }

      // 1. Préparation : Récupérer le fichier et le découper si nécessaire

      // Télécharger le blob source (qu'il soit local ou distant)
      let sourceBlob: Blob;
      try {
        const response = await fetch(clip.url);
        sourceBlob = await response.blob();
      } catch (e) {
        throw new Error("Impossible de lire la vidéo source.");
      }

      // Découper la vidéo à la durée voulue (max 5s)
      // On utilise le trimStart actuel et on limite la durée
      const durationToProcess = Math.min(clip.duration, 5); // 5s max
      const startOffset = clip.trimStart;

      console.log(`Découpage vidéo : start=${startOffset}, duration=${durationToProcess}`);

      // Utiliser FFmpegService pour trimmer
      // Note: FFmpegService doit être chargé. S'il ne l'est pas, getInstance le chargera.
      const trimmedBlob = await FFmpegService.trimVideo(sourceBlob, startOffset, durationToProcess);

      // 2. Upload to GCS (Firebase Storage) for a reliable signed URL
      //    R2 times out for Replicate downloads (10s timeout), GCS is on the same infra
      const trimFileName = `enhance-input/input_trim_${Date.now()}.mp4`;
      const uploadConfig = await getUploadUrl({
        fileName: trimFileName,
        contentType: 'video/mp4'
      });

      const { uploadUrl, gcsUri } = (uploadConfig.data as any);

      await fetch(uploadUrl, {
        method: 'PUT',
        body: trimmedBlob,
        headers: { 'Content-Type': 'video/mp4' }
      });

      console.log("Vidéo découpée uploadée vers GCS:", gcsUri);

      // 3. Appeler la fonction Firebase appropriée (Eye Contact ou Amélioration)
      let enhancedUrl, versionId;

      if (isEyeContact) {
        // Timeout 15 minutes pour éviter deadline-exceeded
        const toggleEyeContact = functions.httpsCallable('toggleEyeContact', { timeout: 3600000 });
        const result: any = await toggleEyeContact({
          gcsUri: gcsUri,
          clipId: clip.id
        });
        enhancedUrl = result.data.enhancedUrl;
        versionId = result.data.versionId || `eye-contact-${Date.now()}`;
        prompt = "Contact Visuel (beta)"; // Override internal prompt name
      } else {
        // Mode Async avec Webhook (plus de polling)
        const startEnhanceVideo = functions.httpsCallable('enhanceVideo'); // Appel rapide (démarrage)

        const startResult: any = await startEnhanceVideo({
          gcsUri: gcsUri,
          prompt: prompt,
          clipId: clip.id
        });

        const { predictionId } = startResult.data;
        if (!predictionId) throw new Error("Pas de predictionId reçu.");

        console.log("[Enhance] Amélioration démarrée, listener Firestore sur:", predictionId);

        // Real-time Firestore listener — no timeout, resolves when status changes
        const waitForCompletion = (): Promise<any> => {
          return new Promise((resolve, reject) => {
            const uid = auth.currentUser?.uid;
            console.log(`[Enhance] waitForCompletion: uid=${uid}, predictionId=${predictionId}`);

            if (!uid) {
              console.error("[Enhance] ERREUR: Utilisateur non authentifié (uid manquant)");
              return reject(new Error("Non authentifié"));
            }

            // Construct reference carefully
            let predRef;
            try {
              predRef = firestore.collection('users').doc(uid).collection('predictions').doc(predictionId);
              console.log(`[Enhance] Listening on path: ${predRef.path}`);
            } catch (pathErr) {
              console.error("[Enhance] Erreur construction path:", pathErr);
              return reject(pathErr);
            }

            // Background poller REMOVED - We rely on Webhook + Firestore Listener
            // The replicateWebhook will update Firestore when done.

            // Firestore listener: resolves immediately when status changes
            const unsubscribe = predRef.onSnapshot((snap) => {
              const data = snap.data();
              console.log(`[Enhance] Firestore snapshot update for ${predictionId}:`, data);
              if (!data) return;

              console.log(`[Enhance] Firestore update: status=${data.status}`);

              if (data.status === 'succeeded') {
                unsubscribe();
                resolve(data);
              } else if (data.status === 'failed' || data.status === 'canceled') {
                unsubscribe();
                reject(new Error(`Amélioration échouée: ${data.error || 'Erreur serveur'}`));
              }
              // 'starting' / 'processing' → keep listening
            }, (err) => {
              unsubscribe();
              reject(err);
            });

            // Store cleanup for cancellation
            (window as any).__enhanceCleanup = () => {
              unsubscribe();
            };
          });
        };

        const finalResult = await waitForCompletion();
        enhancedUrl = finalResult.enhancedUrl;
        versionId = finalResult.versionId;
      }

      // 4. Ajouter la nouvelle version
      const newVersion: VideoVersion = {
        id: versionId,
        url: enhancedUrl,
        prompt: prompt,
        createdAt: Date.now()
      };

      const updatedVersions = [...currentVersions, newVersion];

      // 5. Générer de nouvelles vignettes pour la version améliorée
      console.log('[Enhance] Generating thumbnails for enhanced video...');
      let newThumbnails: string[] = [];
      try {
        newThumbnails = await generateThumbnails(enhancedUrl);
      } catch (thumbErr) {
        console.warn('[Enhance] Failed to generate thumbnails for enhanced video:', thumbErr);
      }

      // 6. Mettre à jour le clip
      console.log('[Enhance] Updating clip with:', { versionId, enhancedUrl, versionsCount: updatedVersions.length });
      updateClips(prev => {
        const updated = prev.map(c => {
          if (c.id === clip.id) {
            console.log('[Enhance] Found clip to update:', c.id);
            const updatedClip = {
              ...c,
              isEnhancingVideo: false,
              isApplyingEyeContact: false,
              videoVersions: updatedVersions,
              activeVideoVersion: versionId,
              url: enhancedUrl,
              thumbnails: newThumbnails.length > 0 ? newThumbnails : c.thumbnails
            };
            console.log('[Enhance] Updated clip:', { activeVideoVersion: updatedClip.activeVideoVersion, url: updatedClip.url });
            return updatedClip;
          }
          return c;
        });
        return updated;
      }, true);

      // 7. Afficher la vidéo améliorée dans le chat
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        text: `✨ **Amélioration terminée !**\n\nPrompt: "${prompt}"\n\nLa version a été automatiquement activée sur le clip.`
      }]);

    } catch (error) {
      console.error("Erreur enhance video:", error);
      updateClips(prev => prev.map(c =>
        c.id === clip.id ? { ...c, isEnhancingVideo: false, isApplyingEyeContact: false } : c
      ), false);
      alert(`Erreur lors de l'amélioration vidéo: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const cancelClipVideoEnhancement = (clip: Clip) => {
    updateClips(prev => prev.map(c =>
      c.id === clip.id ? { ...c, isEnhancingVideo: false } : c
    ), false);
  };

  const selectVideoVersion = async (clip: Clip, versionId: string | undefined) => {
    // Trouver la version correspondante
    let targetUrl = clip.url;

    // Si c'est undefined ou 'original', on cherche la version originale
    if (versionId === undefined || versionId === 'original') {
      const originalVersion = clip.videoVersions?.find(v => v.id === 'original');
      if (originalVersion) {
        targetUrl = originalVersion.url;
        versionId = undefined; // Reset active version ID to undefined for "Original"
      }
    } else {
      const version = clip.videoVersions?.find(v => v.id === versionId);
      if (version) targetUrl = version.url;
    }

    if (!targetUrl) return;

    // Générer de nouvelles vignettes pour la version sélectionnée
    let newThumbnails: string[] = [];
    try {
      newThumbnails = await generateThumbnails(targetUrl);
    } catch (thumbErr) {
      console.warn('[Version] Failed to generate thumbnails for selected version:', thumbErr);
    }

    updateClips(prev => prev.map(c => {
      if (c.id === clip.id) {
        return {
          ...c,
          activeVideoVersion: versionId,
          url: targetUrl,
          thumbnails: newThumbnails.length > 0 ? newThumbnails : c.thumbnails
        };
      }
      return c;
    }), false);
  };

  // Track which master template is being edited (for save-to-library)
  const [editingMasterInfo, setEditingMasterInfo] = useState<{ libraryItem: any; clipId: string } | null>(null);
  const [libraryRefreshTrigger, setLibraryRefreshTrigger] = useState(0);

  const handleAddLibraryItem = async (item: any, category: any, mode?: string) => {
    const currentTime = timelinePlayerRef.current?.getCurrentTime() || 0;
    if (category.id === 'synthes') {
      // Generate fresh unique IDs for default clips
      const defaultClipsWithUniqueIds = DEFAULT_SYNTHE_CLIPS.map((clip, idx) => ({
        ...clip,
        id: `synthe-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
      }));

      const newClip = createNewClip({
        type: 'synthe',
        name: item.name,
        start: currentTime,
        duration: item.duration || 5,
        trackId: 't1',
        syntheData: item.syntheData || { clips: defaultClipsWithUniqueIds, tracks: INITIAL_TRACKS }
      });
      updateClips(prev => [...prev, newClip]);
      if (mode === 'edit') {
        setEditingMasterInfo({ libraryItem: item, clipId: newClip.id });
        navigateIntoClip(newClip);
      }
      return;
    }

    if (category.id === 'panels') {
      // Generate fresh unique IDs for default panel clips
      const defaultPanelClipsWithUniqueIds = DEFAULT_PANEL_CLIPS.map((clip, idx) => ({
        ...clip,
        id: `panel-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
      }));

      const newClip = createNewClip({
        type: 'panel',
        name: item.name,
        start: currentTime,
        duration: item.duration || 5,
        trackId: 'v1',
        syntheData: item.syntheData || { clips: defaultPanelClipsWithUniqueIds, tracks: INITIAL_TRACKS, backgroundColor: '#000000' }
      });
      updateClips(prev => [...prev, newClip]);
      if (mode === 'edit') {
        setEditingMasterInfo({ libraryItem: item, clipId: newClip.id });
        navigateIntoClip(newClip);
      }
      return;
    }

    if (category.id === 'voiceover') {
      const voiceData = item as GeneratedVoice;
      if (voiceData.isLoading || !voiceData.url || !voiceData.duration) return;

      const response = await fetch(voiceData.url);
      const blob = await response.blob();
      const newUrl = URL.createObjectURL(blob);

      const targetTrack = tracks.find(t => t.accepts.includes('audio') && !['t1', 's1', 'z1'].includes(t.id)) || tracks[3];
      if (!targetTrack) {
        console.error("No suitable track for voiceover found");
        return;
      }

      const newClip = createNewClip({
        type: 'audio',
        name: `Voiceover: "${voiceData.text.substring(0, 20)}..."`,
        url: newUrl,
        duration: voiceData.duration,
        originalDuration: voiceData.duration,
        trackId: targetTrack.id,
        start: currentTime,
      });

      updateClips(prev => [...prev, newClip]);

      generateWaveformData(newClip.url!).then(waveform => {
        updateClips(prev => prev.map(c => c.id === newClip.id ? { ...c, waveform } : c), false);
      });

      await generateSubtitlesForClip(newClip, blob, currentProjectId as string);

    } else if (category.id === 'audio') {
      if (!item.url || !item.duration || typeof item.duration !== 'string') return;

      const durationParts = item.duration.split(':').map(Number);
      const durationInSeconds = durationParts.length === 2 ? (durationParts[0] * 60) + durationParts[1] : 0;

      if (durationInSeconds <= 0) {
        console.error('Invalid duration format for library item:', item.duration);
        return;
      };

      if (mode === 'fit') {
        const timelineDuration = Math.max(0, ...allClips.filter(c => c.type !== 'audio').map(c => c.start + c.duration));
        if (timelineDuration <= 0) {
          console.warn("Fit to timeline requires visual content.");
          return;
        }

        const newTrack: Track = {
          id: `track-audio-${Date.now()}`,
          label: ``,
          accepts: ['audio'],
          isMagnetic: false,
          isVisible: true,
          isMuted: false,
        };

        setTracks(prev => {
          const newTracks = [...prev];
          const zoomTrackIndex = newTracks.findIndex(t => t.id === 'z1');
          const insertionIndex = zoomTrackIndex !== -1 ? zoomTrackIndex : newTracks.length;
          newTracks.splice(insertionIndex, 0, newTrack);

          return newTracks.map((track, index) => {
            if (['t1', 's1', 'z1'].includes(track.id)) return track;
            return { ...track, label: `${index - 1}` };
          });
        });

        const newAudioClips: Clip[] = [];
        let cumulativeTime = 0;

        if (durationInSeconds < timelineDuration) { // Loop
          while (cumulativeTime < timelineDuration) {
            const remainingOnTimeline = timelineDuration - cumulativeTime;
            const durationForThisSegment = Math.min(durationInSeconds, remainingOnTimeline);
            newAudioClips.push(createNewClip({
              ...item,
              type: 'audio',
              trackId: newTrack.id,
              start: cumulativeTime,
              duration: durationForThisSegment,
              originalDuration: durationInSeconds,
            }));
            cumulativeTime += durationForThisSegment;
          }
        } else { // Crop
          newAudioClips.push(createNewClip({
            ...item,
            type: 'audio',
            trackId: newTrack.id,
            start: 0,
            duration: timelineDuration,
            originalDuration: durationInSeconds,
          }));
        }

        updateClips(prev => [...prev, ...newAudioClips]);
        generateWaveformData(item.url).then(waveform => {
          updateClips(prev => prev.map(c => newAudioClips.find(nc => nc.id === c.id) ? { ...c, waveform } : c), false);
        });
      } else {
        const targetTrack = tracks.find(t => t.accepts.includes('audio') && !['t1', 's1', 'z1'].includes(t.id)) || tracks[3];
        if (!targetTrack) return;

        const newClip = createNewClip({
          ...item,
          type: 'audio',
          trackId: targetTrack.id,
          start: currentTime,
          duration: durationInSeconds,
          originalDuration: durationInSeconds,
        });
        updateClips(prev => [...prev, newClip]);
        generateWaveformData(item.url).then(waveform => {
          updateClips(prev => prev.map(c => c.id === newClip.id ? { ...c, waveform } : c), false);
        });
      }
      console.warn(`Adding '${item.name}' from the library is not implemented yet.`);
    }
  };


  /* --- Project Loading Fade Effect --- */


  const handleSeekRequest = (time: number) => {
    if (timelinePlayerRef.current) {
      timelinePlayerRef.current.seek(time);
    }
  };

  const handleSelectSidebarProject = async (project: SidebarProject) => {
    // 0. Auto-Save Current Project Logic
    if (currentProjectId) {
      setSavedProjects(prev => prev.map(p => {
        if (p.id === currentProjectId) {
          const updatedProject: SavedProject = {
            ...p,
            clips: allClips, // Save current clips
            tracks: tracks,   // Save current tracks
            montageVersions: montageVersions, // Save current versions
            updatedAt: Date.now()
          };

          // Trigger Firestore save in background (fire and forget)
          saveProjectToFirestore(updatedProject).catch(err =>
            console.error("Auto-save to Firestore failed:", err)
          );

          return updatedProject;
        }
        return p;
      }));
    }

    // 0.B CLEAR PLAYER CANVAS & STOP
    if (timelinePlayerRef.current) {
      timelinePlayerRef.current.pause();
      // @ts-ignore - clearCanvas added via useImperativeHandle
      if (timelinePlayerRef.current.clearCanvas) {
        // @ts-ignore
        timelinePlayerRef.current.clearCanvas();
      }
    }

    // Reset playhead immediately
    if (timelinePlayerRef.current) {
      timelinePlayerRef.current.seek(0);
    }

    // Find the full SavedProject from savedProjects by ID
    const fullProject = savedProjects.find(p => p.id === project.id);

    if (fullProject) {
      loadProject(fullProject);
    } else {
      // Logic for New Project / Unsaved Project (Blank Slate)

      let initialClips: Clip[] = [];

      // 1. Process Assets if available (from Drag & Drop creation)
      if (project.assets && project.assets.length > 0) {

        let cumulativeTime = 0;
        for (const asset of project.assets) {
          const duration = await getMediaDurationFromUrl(asset.url);
          const isVideo = asset.type === 'video';
          const preferredTrackId = isVideo ? 'v2' : 'v1';

          const newClip = createNewClip({
            name: asset.name,
            url: asset.url,
            type: asset.type as Clip['type'],
            trackId: preferredTrackId,
            start: cumulativeTime,
            duration: duration,
            originalDuration: duration,
            waveform: [],
            thumbnails: [],
          });

          initialClips.push(newClip);
          cumulativeTime += duration;
        }

        // Trigger background generation
        initialClips.forEach(async (clip) => {
          if (clip.type === 'video' && clip.url) {
            generateThumbnails(clip.url).then(thumbnails => {
              updateClips(prev => prev.map(c => c.id === clip.id ? { ...c, thumbnails } : c), false);
            });

            // Auto-transcription + R2 Upload
            try {
              const uploadMsgId = `upload-batch-${project.id}`;
              const totalToUpload = initialClips.filter(c => c.type === 'video').length;

              const response = await fetch(clip.url);
              const blob = await response.blob();

              // 1. Auto-Transcribe DISABLED - user can trigger manually from Subtitles tab
              // handleVideoFileDropped(blob, clip, project.id);

              // 2. Background Upload to R2 for persistence
              const fileName = `project_upload_${Date.now()}_${Math.random().toString(36).slice(2)}.mp4`;
              const fileHash = `${clip.name}_${blob.size}_${blob.type}`;

              updateAggregateUploadProgress(uploadMsgId, setMessages, clip.name, 0, totalToUpload);

              // Get Upload URL
              const uploadResult: any = await getR2UploadUrl({
                fileName,
                contentType: blob.type || 'video/mp4',
                fileHash
              });
              const { uploadUrl, publicUrl, existing } = uploadResult.data;

              if (!existing) {
                await fetch(uploadUrl, {
                  method: 'PUT',
                  body: blob,
                  headers: { 'Content-Type': blob.type || 'video/mp4' }
                });
              }

              // Update clip with permanent URL using SAFE update
              updateClipSafe(project.id, clip.id, c => {
                const localUrl = getClipLocalSourceUrl(c);
                return {
                  ...c,
                  url: publicUrl,
                  ...(localUrl ? { localUrl } : {}),
                  isUploading: false
                };
              });

              // Update aggregate progress
              setMessages(prev => {
                const msg = prev.find(m => m.id === uploadMsgId);
                const currentCount = (msg?.progress || 0) / (100 / totalToUpload) + 1; // Basic heuristic tracker
                const isFinal = currentCount >= totalToUpload;

                // For production logic, we'd use a more precise counter in state, 
                // but this works for simple UI persistence.
                updateAggregateUploadProgress(uploadMsgId, setMessages, clip.name, Math.min(currentCount, totalToUpload), totalToUpload, isFinal);
                return prev;
              });

            } catch (e) {
              console.error("Auto-processing failed", e);
            }
          }

          if (clip.type === 'audio' && clip.url) {
            generateWaveformData(clip.url).then(waveform => {
              updateClips(prev => prev.map(c => c.id === clip.id ? { ...c, waveform } : c), false);
            });
          }
        });
      } else {
        // No assets, silent creation or just log to console if needed
      }

      // 2. Create SavedProject Entry
      const newSavedProject: SavedProject = {
        id: project.id,
        name: project.title || "Nouveau projet",
        clips: initialClips,
        tracks: INITIAL_TRACKS.map(t => ({ ...t })),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        thumbnailUrl: project.thumbnailUrl,
      };

      // 3. Save & Load
      setSavedProjects(prev => [newSavedProject, ...prev]);
      loadProject(newSavedProject);
    }

    // Close sidebar after selecting a project
    setSidebarOpen(false);

    // FADE IN

  };

  const handleSubtitleClick = useCallback((subtitle: Subtitle) => {
    setActiveTab('subtitles');

    // Find the clip that contains this subtitle
    const clip = allClips.find(c =>
      c.subtitles &&
      c.subtitles[activeSubtitleLanguage] &&
      c.subtitles[activeSubtitleLanguage].some(s => s.id === subtitle.id)
    );

    if (clip) {
      setSelectedClipId(clip.id);
      setSelectedSubtitleId(subtitle.id);

      // Auto-scroll logic will be handled in SubtitlesTab via selectedSubtitleId
    }
  }, [allClips, activeSubtitleLanguage]);

  const handleOpenTokenStore = () => {
    setActiveTab('account');
  };

  const handleOpenLibrary = () => {
    setActiveTab('library');
  };

  const handleDeleteProject = async (projectId: string) => {
    console.log(`[DeleteProject] Starting deletion for ${projectId}`);
    // Mark project as deleted FIRST to prevent auto-save race condition
    markProjectDeleted(projectId);
    try {
      // 1. Delete from Firestore FIRST (this is the source of truth)
      await deleteProjectFromFirestore(projectId);
      console.log(`[DeleteProject] Firestore deletion complete for ${projectId}`);

      // 2. Update local state - this will trigger the auto-save in useProjectState
      setSavedProjects(prev => {
        const filtered = prev.filter(p => p.id !== projectId);
        console.log(`[DeleteProject] savedProjects: ${prev.length} -> ${filtered.length}`);
        return filtered;
      });

      setRootProjects(prev => {
        const filtered = prev.filter(p => p.id !== projectId);
        console.log(`[DeleteProject] rootProjects: ${prev.length} -> ${filtered.length}`);
        return filtered;
      });

      setFolders(prev => prev.map(f => ({
        ...f,
        projects: f.projects.filter(p => p.id !== projectId)
      })));

      // 3. If we just deleted the currently active project, reset to neutral dropzone
      if (currentProjectId === projectId) {
        console.log(`[DeleteProject] Deleted active project, resetting to dropzone`);
        runWithoutAutoMontageFork(() => {
          updateClips(() => []);
        });
        setCurrentProjectId(null);
        setNavigationStack([]);
        setCachedSourceClips([]);
        currentVersionIdStateRef.current = null;
        setCurrentVersionId(null);
        setSelectedClipId(null);
        montageVersionsRef.current = [];
        setMontageVersions([]);
        setMessages([]);
        navigate('/');
      }

      // The useProjectState hook will auto-save settings after 1s debounce
      console.log(`[DeleteProject] Local state updated for ${projectId}. Auto-save will persist settings.`);

    } catch (error) {
      console.error('[DeleteProject] Failed:', error);
      alert(`Erreur lors de la suppression: ${error}`);
    }
  };


  const navigateIntoClip = useCallback((clip: Clip) => {
    if (clip.type === 'synthe' || clip.type === 'panel') {
      // Initialize syntheData if missing
      if (!clip.syntheData) {
        // Generate fresh unique IDs for default clips based on clip type
        const defaultClipsWithUniqueIds = clip.type === 'synthe'
          ? DEFAULT_SYNTHE_CLIPS.map((c, idx) => ({
            ...c,
            id: `synthe-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
          }))
          : DEFAULT_PANEL_CLIPS.map((c, idx) => ({
            ...c,
            id: `panel-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
          }));

        // We need to update the clip to add empty data
        const initializedClip = {
          ...clip,
          syntheData: {
            clips: defaultClipsWithUniqueIds,
            tracks: INITIAL_TRACKS,
            backgroundColor: clip.type === 'panel' ? '#000000' : undefined
          }
        };
        // Update the clip in the current scope
        updateClips((prev: Clip[]) => prev.map((c: Clip) => c.id === clip.id ? initializedClip : c));
        // Then navigate
        setNavigationStack((prev: Clip[]) => [...prev, initializedClip]);
      } else {
        // If syntheData exists but tracks are empty, initialize them
        if (!clip.syntheData.tracks || clip.syntheData.tracks.length === 0) {
          const fixedClip = {
            ...clip,
            syntheData: { ...clip.syntheData, tracks: INITIAL_TRACKS }
          };
          updateClips((prev: Clip[]) => prev.map((c: Clip) => c.id === clip.id ? fixedClip : c));
          setNavigationStack((prev: Clip[]) => [...prev, fixedClip]);
        } else {
          setNavigationStack((prev: Clip[]) => [...prev, clip]);
        }
      }
      setSelectedClipId(null);
      timelinePlayerRef.current?.seek(0); // Reset time for the new timeline
    }
  }, [updateClips]);

  const navigateUp = useCallback(() => {
    setNavigationStack((prev: Clip[]) => {
      const newStack = prev.slice(0, -1);
      // If returning to root and we were editing a master, clean up the temp clip
      if (newStack.length === 0 && editingMasterInfo) {
        setAllClips(prevClips => prevClips.filter(c => c.id !== editingMasterInfo.clipId));
        setEditingMasterInfo(null);
      }
      return newStack;
    });
    setSelectedClipId(null);
    timelinePlayerRef.current?.seek(0);
  }, [editingMasterInfo]);

  const handleInitialFilesDrop = useCallback(async (files: File[]) => {

    let cumulativeTime = 0;
    const newClips: Clip[] = [];

    if (allClips.length > 0) {
      cumulativeTime = Math.max(0, ...allClips.map(c => c.start + c.duration));
    }

    const sortedFiles = [...files].sort((a, b) => {
      if (a.type.startsWith('video/')) return -1;
      if (b.type.startsWith('video/')) return 1;
      return 0;
    });

    for (const file of sortedFiles) {
      // --- DUPLICATE CHECK ---
      // Check if file with same name and size already exists in current project
      const alreadyExists = allClips.some(c =>
        c.name === file.name &&
        ((c as any)._file?.size === file.size || c.originalDuration > 0)
      );
      if (alreadyExists) {
        console.log(`[Drop] skipping duplicate file: ${file.name}`);
        continue;
      }

      const isVideo = file.type.startsWith('video/');
      const isGif = file.type === 'image/gif';
      const type: Clip['type'] = isGif || isVideo ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'image';

      // CRITICAL FIX: Read video files IMMEDIATELY before any await!
      // Browser can revoke file permissions after ANY await in the event handler
      let cachedArrayBuffer: ArrayBuffer | null = null;
      let skipDefaultProcessing = false;

      if (isVideo && !isGif) {
        // PERF: Skip arrayBuffer for large files (> 1GB) to avoid OOM
        if (file.size > 1024 * 1024 * 1024) {
          console.log(`[Drop] Fichier lourd détecté (${(file.size / 1024 / 1024).toFixed(1)} MB). Processing optimization engaged.`);
          skipDefaultProcessing = true;
        } else {
          try {
            cachedArrayBuffer = await file.arrayBuffer();
            console.log(`[Drop] Fichier ${file.name} lu immédiatement (${(cachedArrayBuffer.byteLength / 1024 / 1024).toFixed(1)} MB)`);
          } catch (e) {
            console.error("Erreur lecture fichier immédiate:", e);
          }
        }
      }

      // Videos go to track '2' ('v2'), other media to track '1' ('v1').
      const preferredTrackId = isVideo ? 'v2' : 'v1';

      let targetTrack = tracks.find(t => t.id === preferredTrackId);
      // Fallback if the preferred track is somehow unsuitable
      if (!targetTrack || !targetTrack.accepts.includes(type)) {
        targetTrack = tracks.find(t => t.accepts.includes(type) && !['t1', 's1', 'z1'].includes(t.id));
      }

      if (!targetTrack) {
        console.warn(`No suitable track found for media of type ${type}. Skipping file ${file.name}.`);
        continue;
      }

      // Create blob URL from cached data if available, otherwise from original file
      // NOTE: For large files, we use the file directly which creates a reference URL (safe)
      const fileUrl = cachedArrayBuffer
        ? URL.createObjectURL(new Blob([cachedArrayBuffer], { type: file.type }))
        : URL.createObjectURL(file);

      const duration = await getMediaDurationFromUrl(fileUrl);

      const newClip = createNewClip({
        name: file.name,
        url: fileUrl,
        localUrl: fileUrl,
        type,
        trackId: targetTrack.id,
        start: cumulativeTime,
        duration,
        originalDuration: duration,
        waveform: [],
        thumbnails: [],
      });
      // Store the original file for R2 upload
      (newClip as any)._file = file;
      // Also store the cached blob if we have it (for cases where file permissions expire)
      if (cachedArrayBuffer) {
        (newClip as any)._cachedBlob = new Blob([cachedArrayBuffer], { type: file.type });
      }
      newClips.push(newClip);
      cumulativeTime += duration;
    } // End of for loop

    if (newClips.length > 0) {
      updateClips(prev => [...prev, ...newClips]);

      // --- Determine Target Project ID UPFRONT ---
      const firstClip = newClips[0];
      const projectName = firstClip.name.split('.').slice(0, -1).join('.') || "Nouveau projet";

      // If we are currently in a project, just add to it. Otherwise, create a new one.
      const isNewProject = !currentProjectId;
      const targetProjectId = currentProjectId || `proj_${Date.now()}`;

      // Upload and Update Clips
      // Upload and Update Clips
      const uploadMsgId = `upload-batch-${targetProjectId}`;
      const videoClips = newClips.filter(c => c.type === 'video' && !c.name.toLowerCase().endsWith('.gif'));

      // Helper for chat logging
      const logToChat = (msg: string) => {
        const time = new Date().toLocaleTimeString('en-US', { hour12: true });
        setMessages(prev => prev.map(m => {
          if (m.id === uploadMsgId) {
            return {
              ...m,
              text: `${m.text}\n[${time}] ${msg}`
            };
          }
          return m;
        }));
      };

      if (videoClips.length > 0) {
        const fileNames = videoClips.map(c => c.name).join(', ');
        const startTime = new Date().toLocaleTimeString('en-US', { hour12: true });
        setMessages(prev => [...prev, {
          id: uploadMsgId,
          role: 'assistant',
          text: `[${startTime}] 📥 ${videoClips.length > 1 ? 'Vidéos reçues' : 'Vidéo reçue'}: **${fileNames}**\n[${startTime}] ☁️ Initialisation...`,
          isLoading: true,
          progress: 0,
          statusText: "Préparation..."
        }]);
      }

      // Track overall progress
      const progressMap = new Map<string, number>();
      let completionLogged = false;

      newClips.forEach(async (clip) => {
        const isGif = clip.name.toLowerCase().endsWith('.gif');
        if (clip.type === 'video' && !isGif && clip.url) {
          // Generate thumbnails
          generateThumbnails(clip.url).then(thumbnails => {
            updateClipSafe(targetProjectId!, clip.id, c => ({ ...c, thumbnails }));
          });

          // Process via handleTimelineFileDrop (includes Upload -> MP3 -> Transcribe)
          const cachedBlob = (clip as any)._cachedBlob as Blob | null;
          const blobOrFile = cachedBlob || (clip as any)._file || ((clip as any)._cachedBlob === null ? null : (clip as any).file);

          if (blobOrFile) {
            handleTimelineFileDrop(blobOrFile, clip, targetProjectId!, {
              logFn: logToChat,
              onProgress: (p, status) => {
                progressMap.set(clip.id, p);

                // Simple average
                let total = 0;
                progressMap.forEach(v => total += v);
                const avg = Math.round(total / videoClips.length);

                setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
                  ...m,
                  statusText: `${status} (Global ${avg}%)`,
                  progress: avg
                } : m));

                if (avg === 100) {
                  if (completionLogged) return;
                  completionLogged = true;
                  const time = new Date().toLocaleTimeString('en-US', { hour12: true });
                  setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
                    ...m,
                    isLoading: false,
                    statusText: "Terminé",
                    text: `${m.text}\n[${time}] ✅ Traitement terminé !`
                  } : m));
                }
              }
            });
          }
        }

        if (clip.type === 'audio' && clip.url) {
          generateWaveformData(clip.url).then(waveform => {
            updateClipSafe(targetProjectId!, clip.id, c => ({ ...c, waveform }));
          });
        }
      });

      // Automatically create OR update project from dropped files
      if (!isNewProject) {
        console.log(`[Drop] Adding files to existing project ${currentProjectId}`);
        // Project ID is the current one, so clips are already in UI state via updateClips
        // We just need to trigger a save or rely on auto-save
        // Auto-save useEffect handles saving whenever `allClips` changes.
        // Optionnally, we could update the thumbnail if there was none.
      } else {
        // Create NEW project
        console.log(`[Drop] Creating new project ${targetProjectId} and naming it: ${projectName}`);

        // Generate thumbnail
        let projectThumbnail = '';
        const firstVisualClip = newClips.find(c => c.type === 'video' || c.type === 'image');

        if (firstVisualClip) {
          if (firstVisualClip.type === 'image') {
            projectThumbnail = firstVisualClip.url;
          } else if (firstVisualClip.type === 'video') {
            try {
              // Generate thumbnail from video
              projectThumbnail = await generateThumbnailFromUrl(firstVisualClip.url);
            } catch (e) {
              console.warn("Failed to generate thumbnail from video", e);
            }
          }
        }

        const newProject: SavedProject = {
          id: targetProjectId,
          name: projectName,
          thumbnailUrl: projectThumbnail,
          clips: [...newClips],
          tracks: tracks,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };

        const sidebarProject: SidebarProject = {
          id: newProject.id,
          title: newProject.name,
          updated: new Date().toLocaleDateString(),
          thumbnailUrl: newProject.thumbnailUrl,
          assets: newClips.map(c => ({
            id: c.id,
            name: c.name,
            type: c.type as any,
            url: c.url,
            size: (c as any)._file?.size || 0
          }))
        };

        setRootProjects(prev => [sidebarProject, ...prev]);
        setSavedProjects(prev => [newProject, ...prev]);
        saveProjectToFirestore(newProject).catch(console.error);

        setCurrentProjectId(newProject.id);
        navigate(`/project/${newProject.id}`);
      }
    }
  }, [tracks, allClips, updateClips, currentProjectId, handleTimelineFileDrop, generateThumbnails, generateWaveformData, updateClipSafe, saveProjectToFirestore]);

  const [scaleFactor, setScaleFactor] = useState(1);
  const [selectedSubtitleId, setSelectedSubtitleId] = useState<number | string | null>(null);

  const handleTimeUpdate = useCallback((time: number) => {
    // Sync active subtitle during video playback
    if (activeSubtitleLanguage && activeSubtitleLanguage !== 'none') {
      const clipWithSubs = allClips.find(c => c.subtitles && c.subtitles[activeSubtitleLanguage]);
      if (clipWithSubs) {
        const activeSub = clipWithSubs.subtitles[activeSubtitleLanguage].find(s => time >= s.start && time <= s.end && !s.hidden);
        if (activeSub && activeSub.id !== selectedSubtitleId) {
          setSelectedSubtitleId(activeSub.id);
        } else if (!activeSub && selectedSubtitleId !== null) {
          setSelectedSubtitleId(null);
        }
      }
    }
  }, [allClips, activeSubtitleLanguage, selectedSubtitleId]);

  const handleRenameProject = async (project: SidebarProject, newName: string) => {
    try {
      if (!newName || newName.trim() === project.title) return;

      const title = newName.trim();
      const updatedDate = new Date().toLocaleDateString();

      // 1. Update SavedProjects (Source of Truth) carefully
      // We must find the FULL project object to avoid losing 'clips' and 'tracks'
      setSavedProjects(prev => {
        return prev.map(p => {
          if (p.id === project.id) {
            return {
              ...p,
              // SavedProject uses 'name' and 'updatedAt'
              name: title,
              updatedAt: Date.now(),
              // Ensure we don't accidentally add 'title' or 'updated' if they shouldn't exist on SavedProject type,
              // but for safety we can keep them if they were already there or just omit them.
              // Best to stick to the interface.
            };
          }
          return p;
        });
      });

      // 2. Update Sidebar Helpers (Root Projects OR Folders)

      // Update Root Projects
      setRootProjects(prev => {
        // Only update if it exists in root
        if (prev.some(p => p.id === project.id)) {
          return prev.map(p => p.id === project.id ? { ...p, title, updated: updatedDate } : p);
        }
        return prev;
      });

      // Update Folders (Nested Children)
      setFolders(prev => {
        return prev.map(folder => {
          // Check if project is in this folder
          // Guard against undefined projects array
          const projects = folder.projects || [];
          const hasProject = projects.some(c => c.id === project.id);

          if (hasProject) {
            return {
              ...folder,
              projects: projects.map(c => c.id === project.id ? { ...c, title, updated: updatedDate } : c)
            };
          }
          return folder;
        });
      });

      // 3. Update Current Project if it's the one open
      // 'setProject' is not defined; updates to 'savedProjects' should propagate if currentProject is derived.
      // If we needed to update local project state, we should rely on the savedProjects or a dedicated fetch.
      // Given we updated savedProjects above, skipping this manual local set.

      await renameProjectInFirestore(project.id, title);

    } catch (error) {
      console.error('[RenameProject] Failed:', error);
    }
  };


  const handleManualSave = useCallback(async () => {
    // If editing a master template, save to library instead of project
    if (editingMasterInfo) {
      // Use currentScope to get the actual clips/tracks being edited
      const scopeClips = currentScope.clips;
      const scopeTracks = currentScope.tracks;
      try {
        const updatedTemplate = {
          ...editingMasterInfo.libraryItem,
          syntheData: {
            clips: scopeClips,
            tracks: scopeTracks,
            ...(editingMasterInfo.libraryItem?.syntheData?.backgroundColor ? { backgroundColor: editingMasterInfo.libraryItem.syntheData.backgroundColor } : {})
          }
        };
        await saveMasterTemplate(updatedTemplate);
        setEditingMasterInfo(prev => prev ? { ...prev, libraryItem: updatedTemplate } : null);
        console.log('[App] Master template saved:', updatedTemplate.name, 'clips:', scopeClips.length);
        // Refresh library to reflect saved changes
        setLibraryRefreshTrigger(prev => prev + 1);
        // Visual feedback
        const el = document.createElement('div');
        el.textContent = `✓ Master "${updatedTemplate.name}" sauvegardé (${scopeClips.length} éléments)`;
        el.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#22c55e;color:white;padding:10px 20px;border-radius:8px;z-index:9999;font-weight:bold;';
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 2500);
      } catch (err) {
        console.error('[App] Master template save failed:', err);
        alert('Erreur lors de la sauvegarde du master.');
      }
      return;
    }

    if (!currentProjectId) return;

    console.log('[App] Manual Save triggered');
    const project = savedProjects.find(p => p.id === currentProjectId);
    if (!project) return;

    // Upload blobs in the full tree before saving
    const uploadMediaRecursive = async (clips: Clip[]): Promise<Clip[]> => {
      return await Promise.all(clips.map(async (clip) => {
        let updatedClip = { ...clip };

        if (updatedClip.syntheData && updatedClip.syntheData.clips) {
          updatedClip.syntheData = {
            ...updatedClip.syntheData,
            clips: await uploadMediaRecursive(updatedClip.syntheData.clips)
          };
        }

        if (updatedClip.url && updatedClip.url.startsWith('blob:')) {
          try {
            console.log(`[ManualSave] Uploading ${updatedClip.name} to R2...`);
            const response = await fetch(updatedClip.url);
            const blob = await response.blob();

            let extension = '';
            const type = blob.type || '';
            if (type.includes('image')) extension = '.png';
            else if (type.includes('video')) extension = '.mp4';
            else if (type.includes('audio')) extension = '.wav';

            const fileName = updatedClip.name.includes('.') ? updatedClip.name : `${updatedClip.name}${extension}`;
            const fileHash = `${updatedClip.name}_${blob.size}_${blob.type}`.replace(/[^a-zA-Z0-9_.-]/g, '_');

            let mimeType = blob.type || (fileName.endsWith('.mp4') ? 'video/mp4' :
              (fileName.endsWith('.png') ? 'image/png' :
                (fileName.endsWith('.wav') ? 'audio/wav' :
                  (fileName.endsWith('.mp3') ? 'audio/mpeg' : 'application/octet-stream'))));

            if (mimeType.includes(';')) mimeType = mimeType.split(';')[0];

            const uploadConfig = await getR2UploadUrl({ fileName, contentType: mimeType, fileHash });
            const configData = uploadConfig.data as { uploadUrl?: string; publicUrl: string; existing?: boolean };

            if (configData.existing) {
              const localUrl = getClipLocalSourceUrl(updatedClip);
              if (localUrl) {
                updatedClip.localUrl = localUrl;
              }
              updatedClip.url = configData.publicUrl;
              return updatedClip;
            }

            const { uploadUrl, publicUrl } = configData as { uploadUrl: string; publicUrl: string };

            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('PUT', uploadUrl, true);
              xhr.setRequestHeader('Content-Type', mimeType);
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`Upload failed with status ${xhr.status}`));
              };
              xhr.onerror = () => reject(new Error('Network error during upload'));
              xhr.send(blob);
            });

            const localUrl = getClipLocalSourceUrl(updatedClip);
            if (localUrl) {
              updatedClip.localUrl = localUrl;
            }
            updatedClip.url = publicUrl;
            console.log(`[ManualSave] Uploaded ${updatedClip.name} -> ${publicUrl}`);
          } catch (e) {
            console.error(`[ManualSave] Failed to upload ${updatedClip.name}`, e);
          }
        }
        return updatedClip;
      }));
    };

    // allClips is already the root project tree managed by useClipManagement
    const finalUploadedClips = await uploadMediaRecursive(allClips);

    // Also process all Montage Versions clips to replace blob URLs
    const newMontageVersions = await Promise.all(
      montageVersions.map(async (mv) => ({
        ...mv,
        clips: await uploadMediaRecursive(mv.clips)
      }))
    );

    // Update local state allClips so that UI switches to R2 URLs
    setAllClips(finalUploadedClips);

    const updatedProject: SavedProject = {
      ...project,
      clips: finalUploadedClips,
      tracks: navigationStack.length === 0 ? tracks : project.tracks, // Root tracks
      montageVersions: newMontageVersions,
      messages: messages,
      aiSuggestions: editingSuggestions,
      exports: exportedVideos.map(v => ({
        id: v.id,
        name: v.name,
        urlMP4: v.urlMP4,
        urlWebM: v.urlWebM,
        thumbnailUrl: v.thumbnailUrl
      })),
      updatedAt: Date.now()
    };

    try {
      await saveProjectToFirestore(updatedProject);
      console.log('[App] Manual Save success');
      // Update local state is handled by setSavedProjects in other places but explicit sync is good
      setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? updatedProject : p));
    } catch (e) {
      console.error('[App] Manual Save failed', e);
    }
  }, [currentProjectId, savedProjects, allClips, tracks, navigationStack, montageVersions, messages, editingSuggestions, exportedVideos, editingMasterInfo, currentScope]);

  // Master save + exit: saves master content via handleManualSave then navigates up
  const handleMasterSaveAndExit = useCallback(async () => {
    await handleManualSave();
    navigateUp();
  }, [handleManualSave, navigateUp]);

  const [shareToast, setShareToast] = useState<{ type: 'success' | 'error'; message: string; url?: string } | null>(null);

  const handleShareProject = async (project: SidebarProject) => {
    try {
      const fullProject = savedProjects.find(p => p.id === project.id);
      if (!fullProject) {
        setShareToast({ type: 'error', message: 'Projet introuvable.' });
        setTimeout(() => setShareToast(null), 4000);
        return;
      }

      // Retry logic
      let url: string | null = null;
      let lastError: any = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          url = await shareProject(fullProject);
          break;
        } catch (err) {
          lastError = err;
          console.warn(`[ShareProject] Attempt ${attempt + 1} failed:`, err);
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }

      if (!url) {
        throw lastError || new Error('Partage échoué après 3 tentatives');
      }

      // Copy to clipboard with fallback
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.cssText = 'position:fixed;opacity:0;left:-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      // Show toast
      setShareToast({ type: 'success', message: '✅ Lien copié dans le presse-papier !', url });
      setTimeout(() => setShareToast(null), 8000);
    } catch (error) {
      console.error('[ShareProject] Failed:', error);
      setShareToast({ type: 'error', message: '❌ Erreur lors du partage. Réessayez.' });
      setTimeout(() => setShareToast(null), 5000);
    }
  };

  return (
    <PasswordGate>
      <div
        className="fixed inset-0 w-full h-full bg-black flex flex-col overflow-hidden"
        style={{
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'top left',
          width: `${100 / scaleFactor}%`,
          height: `${100 / scaleFactor}%`
        }}
      >
        {/* Sidebar */}
        <SidebarProjectList
          sidebarOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(prev => !prev)}
          folders={folders}
          setFolders={setFolders}
          rootProjects={rootProjects}
          setRootProjects={setRootProjects}
          onSelectProject={(p) => {
            // Check if this project already exists in savedProjects
            const existsInSaved = savedProjects.some(sp => sp.id === p.id);

            if (!existsInSaved) {
              // This is a new project from sidebar - create it in savedProjects with empty clips
              const newSavedProject: SavedProject = {
                id: p.id,
                name: p.title || "Nouveau projet",
                clips: [],
                tracks: INITIAL_TRACKS.map(t => ({ ...t })),
                createdAt: Date.now(),
                updatedAt: Date.now(),
                thumbnailUrl: p.thumbnailUrl,
              };
              setSavedProjects(prev => [newSavedProject, ...prev]);
              // Persist immediately so the route stays resolvable after navigation or reload.
              saveProjectToFirestore(newSavedProject).catch(err => {
                console.error('[ProjectNav] Failed to persist new sidebar project:', err);
              });
            }

            navigate(`/project/${p.id}`);
          }}
          onDeleteProject={handleDeleteProject}
          onRenameProject={handleRenameProject}
          onDuplicateProject={(p) => {
            const full = savedProjects.find(sp => sp.id === p.id);
            if (full) handleDuplicateProject(full);
          }}
          onUpdateProject={async (p) => {
            const fullProject = savedProjects.find(sp => sp.id === p.id);
            if (!fullProject) {
              alert("Projet introuvable en mémoire.");
              return;
            }

            const isCurrentProject = fullProject.id === currentProjectId;
            const clipsToProcess = isCurrentProject ? allClips : fullProject.clips;
            let updatedCount = 0;
            let streamUpdateCount = 0;

            // 1. Process clips recursively (root + nested synthe/panel clips)
            const processClipRecursive = async (inputClip: Clip): Promise<{
              clip: Clip;
              modified: boolean;
              updatedCount: number;
              streamUpdateCount: number;
            }> => {
              let clip = { ...inputClip };
              let modified = false;
              let clipDirectlyUpdated = false;
              let localUpdatedCount = 0;
              let localStreamUpdateCount = 0;

              if (clip.subtitles) {
                const newSubtitles: Record<string, any> = {};
                let subModified = false;

                for (const [lang, subs] of Object.entries(clip.subtitles)) {
                  if (Array.isArray(subs)) {
                    newSubtitles[lang] = subs.map((sub: any, index: number) => {
                      if (typeof sub.id === 'string' && isNaN(Number(sub.id))) {
                        subModified = true;
                        return { ...sub, id: index }; // Force numeric ID
                      }
                      return sub;
                    });
                  } else {
                    newSubtitles[lang] = subs;
                  }
                }

                if (subModified) {
                  clip.subtitles = newSubtitles;
                  modified = true;
                  clipDirectlyUpdated = true;
                }
              }

              const hasValidStreamUrl = clip.streamUrl && (clip.streamUrl.includes('cloudflarestream.com') || clip.streamUrl.includes('videodelivery.net'));

              // Fix Stream URL for videos — upload to Cloudflare Stream if missing
              if (clip.type === 'video' && clip.url && !clip.url.startsWith('blob:') && !hasValidStreamUrl) {

                console.log(`[Updater] Missing streamUrl for clip ${clip.id} (${clip.name}). URL: ${clip.url.substring(0, 50)}... Queuing upload...`);
                try {
                  const exportClient = getServerExportClient(auth.currentUser?.uid || 'anonymous');
                  const streamResult = await exportClient.uploadToStream(
                    clip.url,
                    clip.id
                  );
                  if (streamResult.success && streamResult.streamUrl) {
                    console.log(`[Updater] Uploaded ${clip.id} to Stream: ${streamResult.streamUrl}`);
                    clip.streamUrl = streamResult.streamUrl;

                    if (clip.chunks && clip.chunks.length > 0) {
                      // Retire les chunks locaux si on a un flux Cloudflare propre, pour éviter les conflits dans le lecteur
                      delete clip.chunks;
                    }

                    localStreamUpdateCount++;
                    modified = true;
                    clipDirectlyUpdated = true;
                  }
                } catch (e) {
                  console.warn(`[Updater] Failed to migrate streamUrl for clip ${clip.id}`, e);
                }
              } else if (clip.type === 'video' && hasValidStreamUrl && clip.chunks && clip.chunks.length > 0) {
                // Même si l'URL est valide, le lecteur va prioriser les chunks (qui laguouillent). 
                // On supprime les chunks pour forcer l'utilisation de streamUrl.
                console.log(`[Updater] Clip ${clip.id} a déjà un streamUrl Cloudflare mais garde ses chunks. Nettoyage des chunks...`);
                delete clip.chunks;
                modified = true;
                clipDirectlyUpdated = true;
                localUpdatedCount++; // Compte comme fichier réparé
              }

              if (clip.syntheData?.clips?.length) {
                const processedNestedClips: Clip[] = [];
                let nestedModified = false;

                for (const nestedClip of clip.syntheData.clips) {
                  const nestedResult = await processClipRecursive(nestedClip);
                  processedNestedClips.push(nestedResult.clip);
                  if (nestedResult.modified) nestedModified = true;
                  localUpdatedCount += nestedResult.updatedCount;
                  localStreamUpdateCount += nestedResult.streamUpdateCount;
                }

                if (nestedModified) {
                  clip.syntheData = {
                    ...clip.syntheData,
                    clips: processedNestedClips
                  };
                  modified = true;
                }
              }

              if (clipDirectlyUpdated) {
                localUpdatedCount++;
              }

              return { clip, modified, updatedCount: localUpdatedCount, streamUpdateCount: localStreamUpdateCount };
            };

            const updatedClips: Clip[] = [];
            for (const sourceClip of clipsToProcess) {
              const result = await processClipRecursive(sourceClip);
              updatedClips.push(result.clip);
              updatedCount += result.updatedCount;
              streamUpdateCount += result.streamUpdateCount;
            }

            if (updatedCount === 0 && streamUpdateCount === 0) {
              alert("Le projet est déjà à jour (aucun clip à corriger ou streamer) !");
              return;
            }

            // Save the updates
            if (isCurrentProject) {
              setAllClips(updatedClips);
            } else {
              setSavedProjects(prev => prev.map(sp =>
                sp.id === fullProject.id ? { ...sp, clips: updatedClips, updatedAt: Date.now() } : sp
              ));
            }

            alert(`Mise à jour terminée. ${updatedCount} clips corrigés (${streamUpdateCount} optimisés Stream).`);
          }}
          onShareProject={handleShareProject}
          onOpenStore={handleOpenTokenStore}
          onUploadAsset={uploadAssetToStorage}
          autoCloseOnSelect={false}
          currentProjectId={currentProjectId}
        />

        {/* Main content - offset when sidebar is open */}
        <div
          className="flex flex-row gap-4 flex-1 min-h-0 transition-all duration-300"
          style={{ marginLeft: sidebarOpen ? '320px' : '64px' }}
        >
          <div className="flex-grow flex min-w-0">
            {activeTab === 'admin' ? (
              <AdminDashboard />
            ) : isInitialDropzoneVisible ? (
              <InitialDropzone
                onDropFiles={handleInitialFilesDrop}
              />
            ) : (
              <TimelinePlayer
                ref={timelinePlayerRef}
                onOpenExportMenu={handleOpenExportMenu}
                subtitleStyle={subtitleStyle}
                activeSubtitleLanguage={activeSubtitleLanguage}
                onActiveSubtitleLanguageChange={setActiveSubtitleLanguage}
                onVideoFileDropped={handleTimelineFileDrop}
                onEnhanceClipAudio={enhanceClipAudio}
                onSelectAudioSource={handleSelectAudioSource}
                onClipSelect={onClipSelect}
                onSubtitleClick={handleSubtitleClick}
                onTextOverlayDoubleClick={() => setActiveTab('assistant')}
                onTimeUpdate={handleTimeUpdate}
                aspectRatio={exportAspectRatio}
                onAspectRatioChange={setExportAspectRatio}
                scaleMode={scaleMode}
                onScaleModeChange={setScaleMode}
                tracks={currentScope.tracks}
                setTracks={(newTracks) => {
                  if (navigationStack.length === 0) {
                    setTracks(newTracks);
                  } else {
                    // Update tracks of the active synthé
                    setAllClips((prev: Clip[]) => {
                      const stackIds = navigationStack.map(c => c.id);
                      const updateRecursive = (currentClips: Clip[], depth: number): Clip[] => {
                        if (depth >= stackIds.length) return currentClips;
                        const targetId = stackIds[depth];
                        return currentClips.map(clip => {
                          if (clip.id === targetId) {
                            if (depth === stackIds.length - 1) {
                              return {
                                ...clip,
                                syntheData: { ...clip.syntheData!, tracks: typeof newTracks === 'function' ? newTracks(clip.syntheData!.tracks) : newTracks }
                              };
                            }
                            return {
                              ...clip,
                              syntheData: { ...clip.syntheData!, clips: updateRecursive(clip.syntheData?.clips || [], depth + 1) }
                            };
                          }
                          return clip;
                        });
                      };
                      return updateRecursive(prev, 0);
                    });
                  }
                }}
                allClips={currentScope.clips}
                onClipsChange={updateClips}
                selectedClipId={selectedClipId}
                setSelectedClipId={setSelectedClipId}
                historyIndex={historyIndex}
                historyLength={history.length}
                undo={undo}
                redo={redo}
                isExporting={isExporting}
                montageVersions={montageVersions}
                currentVersionId={currentVersionId}
                onSelectVersion={handleSelectVersion}
                onNavigateInto={navigateIntoClip}
                onNavigateUp={navigateUp}
                canNavigateUp={navigationStack.length > 0}
                navigationBreadcrumbs={navigationStack.map(c => c.name)}
                onSaveProject={handleManualSave}
                isRestrictedMode={currentScope.type === 'synthe' || currentScope.type === 'panel'}
                projectLoadingState={projectLoadingState}
                onTranslateSubtitles={handleTranslateSubtitles}
                onGenerateSubtitles={handleGenerateSubtitles}
                translationsInProgress={translationsInProgress}
              />
            )}
          </div>




          <RightMenu
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onAddLibraryItem={handleAddLibraryItem}
            preloadedAssets={preloadedAssets}
            isLibraryAssetsComplete={isLibraryAssetsComplete}
            onMasterSaveAndExit={editingMasterInfo ? handleMasterSaveAndExit : undefined}
            libraryRefreshTrigger={libraryRefreshTrigger}
            magicState={magicState}
            startMagicProcess={handleStartMagicProcess}
            isExporting={isExporting}
            exportStatus={exportStatus}
            exportProgress={exportProgress}
            currentScope={currentScope}
            isRestrictedMode={currentScope.type === 'synthe' || currentScope.type === 'panel'}
            onSaveMaster={async (data) => {
              if (!user) {
                alert("Vous devez être connecté pour sauvegarder un template.");
                return;
              }
              try {
                const template: MasterTemplate = {
                  id: currentScope.id?.includes('template') ? currentScope.id : `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  name: data.name,
                  type: currentScope.type,
                  syntheData: {
                    clips: currentScope.syntheData?.clips || [],
                    tracks: currentScope.syntheData?.tracks || [],
                    backgroundColor: currentScope.syntheData?.backgroundColor
                  },
                  ownerId: user.uid,
                  createdAt: Date.now()
                };

                await saveMasterTemplate(template);

                setMessages(prev => [...prev, {
                  id: Date.now(),
                  role: 'assistant',
                  text: `✅ Modèle Master "${data.name}" enregistré avec succès dans le cloud.`
                }]);
              } catch (error) {
                console.error("Save master error:", error);
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  role: 'assistant',
                  text: `❌ Erreur lors de la sauvegarde : ${error instanceof Error ? error.message : "Erreur inconnue"}`
                }]);
              }
            }}
            exportDetails={exportDetails}
            exportedVideos={exportedVideos}
            onStartExport={handleStartExport}
            onCancelExport={handleCancelExport}
            exportAspectRatio={exportAspectRatio}
            onExportAspectRatioChange={setExportAspectRatio}
            isTimelineEligibleForFastExport={isTimelineEligibleForFastExport}
            vfxSegmentPreviews={vfxSegmentPreviews}
            onStartServerExport={handleStartServerExport}
            onStartHybridExport={handleStartHybridExport}
            hasTimelineOverlays={hasTimelineOverlays}
            totalTimelineDuration={totalTimelineDuration}
            activeServerExports={activeServerExports}
            onCancelServerExport={handleCancelServerExport}
            onDeleteExport={handleDeleteExport}
            chunks={chunks}
            isMerging={isMerging}
            onRetryChunk={retryChunkDownload}
            subtitleStyle={subtitleStyle}
            setSubtitleStyle={setSubtitleStyle}
            onSeekRequest={handleSeekRequest}
            activeSubtitleLanguage={activeSubtitleLanguage}
            onActiveSubtitleLanguageChange={setActiveSubtitleLanguage}
            messages={messages}
            onSendMessage={handleSendMessage}
            onChoiceSelect={handleEditChoice}
            onTranscriptionModeChange={setUseGpt4oTranscribe}
            editingSuggestions={editingSuggestions.length > 0 ? editingSuggestions : contextualEditingSuggestions}
            isClipContextMenuOpen={isClipContextMenuOpen}
            onCloseClipContextMenu={handleCloseClipContextMenu}
            selectedClip={selectedClip}
            onClipVolumeChange={(volume) => selectedClip && handleClipVolumeChange(selectedClip.id, volume, false)}
            onClipVolumeCommit={(volume) => selectedClip && handleClipVolumeChange(selectedClip.id, volume, true)}
            onEnhanceClipAudio={enhanceClipAudio}
            onSelectAudioSource={handleSelectAudioSource}
            onDeleteAudioSource={handleDeleteAudioSource}
            onEnhanceVideo={enhanceClipVideo}
            onCancelEnhanceVideo={cancelClipVideoEnhancement}
            onSelectVideoVersion={selectVideoVersion}
            onGenerateImageMask={handleGenerateImageMaskFromCanvas}
            onCaptureFrameForMask={handleCaptureFrameForMask}
            onToggleImageMask={handleToggleImageMask}
            onDeleteImageMask={handleDeleteImageMask}
            onAddImageMaskToTimeline={handleAddImageMaskToTimeline}
            allClips={allClips}
            setAllClips={updateClips}
            selectedClipId={selectedClipId}
            setSelectedClipId={setSelectedClipId}
            tracks={tracks}
            currentTime={0}
            user={user}
            projectName={savedProjects.find(p => p.id === currentProjectId)?.name || rootProjects.find(p => p.id === currentProjectId)?.name || 'Projet sans titre'}
            onTranslateSubtitles={handleTranslateSubtitles}
            onGenerateSubtitles={handleGenerateSubtitles}
            translationsInProgress={translationsInProgress}
            scaleFactor={scaleFactor}
            setScaleFactor={setScaleFactor}
            generatedVoices={generatedVoices}
            setGeneratedVoices={setGeneratedVoices}
            voiceoverText={voiceoverText}
            setVoiceoverText={setVoiceoverText}
            selectedSubtitleId={selectedSubtitleId}
          />
        </div>

        {audioExtractionState.isVisible && (
          <AudioExtractionModal
            fileName={audioExtractionState.fileName}
            progress={audioExtractionState.progress}
            statusMessage={audioExtractionState.statusMessage}
          />
        )}

        {/* RelinkModal temporarily disabled
        {isRelinkModalOpen && (
           <RelinkModal ... />
        )}
        */}

        {/* Share Toast Popup */}
        {shareToast && (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-bottom-4 fade-in duration-300"
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            <div className={`rounded-xl shadow-2xl px-5 py-4 flex items-center gap-3 max-w-lg ${shareToast.type === 'success'
              ? 'bg-gray-900 text-white border border-gray-700'
              : 'bg-red-600 text-white'
              }`}>
              <span className="text-sm font-medium">{shareToast.message}</span>
              {shareToast.url && (
                <code className="text-xs bg-white/10 px-2 py-1 rounded font-mono truncate max-w-[200px]">
                  {shareToast.url}
                </code>
              )}
              <button
                onClick={() => setShareToast(null)}
                className="ml-2 text-white/60 hover:text-white text-lg leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </PasswordGate>
  );
}
